import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Activity, AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, BarChart3, Briefcase, Check, ChevronDown, Copy, Download, EyeOff,
  FileCheck2, GripVertical, History, Landmark, Layers, LineChart as LineIcon, ListChecks, Lock, Maximize2, PieChart, RefreshCw,
  Search, Settings2, ShieldCheck, TrendingDown, TrendingUp, UserMinus, Users, Wallet, X, Zap, PiggyBank,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import http from '../api/http';

/* ═════════ Configuration ═════════ */
const YEAR = new Date().getFullYear();
const LIVE_EVERY = 90_000; // actualisation automatique des blocs « vivants » (périodes, contrôles, virements…)

const C = {
  navy: '#0B1437', navy2: '#18246B', y: '#FFC93C', teal: '#25B8A8', coral: '#FF7A6B',
  vio: '#8B7CF6', sky: '#4EA8FF', ink: '#0B1437', mut: '#667091', line: '#E2E6F2', bg: '#F1F3FA',
};
const DISPLAY = "'Bricolage Grotesque','DM Sans',system-ui,sans-serif";
const BODY = "'DM Sans',system-ui,-apple-system,'Segoe UI',sans-serif";
const PALETTE = [C.sky, C.teal, C.y, C.vio, C.coral];
const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

// Droits d'affichage des boutons d'action. Le serveur DOIT refaire ces contrôles (l'UI n'est pas une sécurité).
// Le profil peut se trouver dans plusieurs champs selon la source (login, /me, token) : on les regroupe TOUS
// (avant, seul le premier champ non vide était lu : un `role` générique masquait `libprofil`).
// Cette liste doit rester identique à ROLE_FIELDS dans paie-pilotage.js.
const ROLE_FIELDS = ['role', 'role_code', 'role_name', 'role_label', 'profil', 'libprofil', 'libelle_profil', 'nom_profil', 'profil_nom', 'profil_libelle'];
const roleOf = (u) => (u ? ROLE_FIELDS : [])
  .map((k) => { const v = u[k]; return v && typeof v === 'object' ? Object.values(v).join(' ') : (v ?? ''); })
  .join(' ').toLowerCase().trim();
const IS_ADMIN = /admin/;
const IS_PRESIDENT = /pr[eé]sident|pdg/;
const IS_DIRECTION = /\bdg\b|direction|directeur/;
const RULES = {
  validerContrat: /rh|drh|dg|direction|admin|directeur/,
  controlerVirement: /pr[eé]sident|pdg|admin|directeur/,
  genererOrdre: /pr[eé]sident|pdg|admin|directeur/,
  // Approbation : Direction générale / Directeur (un Président n'approuve pas) · + admin
  approuver: { test: (r) => IS_ADMIN.test(r) || (IS_DIRECTION.test(r) && !IS_PRESIDENT.test(r)) },
  // Clôture définitive : Président · + admin (le Directeur ne clôture pas)
  cloturer: { test: (r) => IS_ADMIN.test(r) || IS_PRESIDENT.test(r) },
};
const profilDefaut = (u) => (/pr[eé]sident|pdg/.test(roleOf(u)) ? 'president' : /\bdg\b|direction|directeur/.test(roleOf(u)) ? 'dg' : 'rh');

/* ═════════ Catalogue des widgets ═════════ */
// span = largeur par défaut (sur 12 colonnes) · min = largeur minimale pour que le contenu reste lisible
// src = sources OBLIGATOIRES (le widget affiche une erreur + « Réessayer » sans elles) · opt = sources facultatives
const WIDGETS = {
  kpis: { title: 'Indicateurs clés', icon: Activity, color: C.sky, span: 12, min: 6, bare: true, src: ['rh', 'masse'], opt: ['effectifs', 'periodes', 'interim'], description: 'Vue d\'ensemble des indicateurs RH et financiers clés de l\'entreprise.' },
  masse: { title: 'Salaire net · évolution mensuelle', icon: Wallet, color: C.teal, span: 8, min: 6, src: ['masse'], description: 'Évolution de la masse salariale nette sur les 12 derniers mois.' },
  contrats_repart: { title: 'Répartition des contrats', icon: PieChart, color: C.vio, span: 4, min: 4, src: ['rh'], description: 'Répartition des effectifs par type de contrat (CDI, CDD, Stagiaire, etc.).' },
  effectif_evol: { title: 'Évolution des effectifs', icon: LineIcon, color: C.sky, span: 6, min: 4, src: ['rh'], description: 'Évolution mensuelle des effectifs sur les 12 derniers mois.' },
  effectif_bu: { title: 'Effectif par BU', icon: BarChart3, color: C.y, span: 6, min: 4, src: ['rh'], description: 'Répartition des effectifs par Business Unit / Département.' },
  pyramide_ages: { title: 'Pyramide des âges et parité', icon: Users, color: C.vio, span: 6, min: 4, src: ['pyramide_ages'], description: 'Vision claire de la diversité et anticipation des besoins de renouvellement des compétences.' },
  cooptation: { title: 'Taux de cooptation', icon: Briefcase, color: C.teal, span: 6, min: 4, src: ['cooptation'], description: 'Part des recrutements réalisés via recommandations de salariés. Réduit coûts et délais.' },
  responsabilites: { title: 'Responsabilités', icon: ShieldCheck, color: C.sky, span: 6, min: 4, src: ['responsabilites'], description: 'Nombre moyen de subordonnés directs par manager. Évalue la capacité de gestion et de motivation d\'équipe.' },
  masse_activite: { title: 'Masse salariale par activité', icon: PieChart, color: C.y, span: 6, min: 4, src: ['masse_activite'], description: 'Répartition de la masse salariale (brut + charges patronales) par activité selon la filiale.' },
  periodes: { title: 'Validation des périodes de paie', icon: ShieldCheck, color: C.teal, span: 7, min: 6, src: ['periodes'], opt: ['controles'], description: 'Validation des périodes de paie, approbation DG et clôture par le Président.' },
  controles: { title: 'Contrôles avant approbation', icon: ListChecks, color: C.coral, span: 5, min: 4, src: ['controles'], description: 'Contrôles automatisés avant approbation de la paie.' },
  interim: { title: 'Paie intérim', icon: Briefcase, color: C.coral, span: 7, min: 6, src: ['interim'], description: 'Gestion et suivi de la paie intérimaire.' },
  cout: { title: 'Coût de la paie · normale vs intérim', icon: Layers, color: C.teal, span: 5, min: 4, src: ['interim'], description: 'Comparaison des coûts entre la paie normale et la paie intérim.' },
  contrats: { title: 'Contrats à valider', icon: FileCheck2, color: C.y, span: 7, min: 4, src: ['contrats'], description: 'Liste des contrats en attente de validation par les RH.' },
  contrats_expiration: { title: 'Contrats arrivant à expiration', icon: AlertTriangle, color: C.coral, span: 6, min: 4, src: ['contrats_expiration'], description: 'Alerte sur les contrats arrivant à expiration selon l\'horizon configuré.' },
  caisse_sociale: { title: 'Caisse sociale', icon: PiggyBank, color: C.y, span: 6, min: 4, src: ['caisse_sociale'], description: 'Montant total de la caisse sociale comparé au mois précédent.' },
  turnover_evol: { title: 'Évolution du turnover', icon: TrendingDown, color: C.coral, span: 6, min: 4, src: ['turnover_evol'], description: 'Courbe d\'évolution mensuelle des départs sur les 12 derniers mois.' },
  turnover_motif: { title: 'Répartition par motif de sortie', icon: PieChart, color: C.vio, span: 6, min: 4, src: ['turnover_motif'], description: 'Répartition des départs par motif de sortie (démission, licenciement, fin de contrat, etc.).' },
  logs: { title: 'Dernières actions', icon: History, color: C.sky, span: 5, min: 4, src: ['logs'], description: 'Historique des 10 dernières actions effectuées sur la plateforme.' },
  turnover_departement: { title: 'Sorties par département', icon: BarChart3, color: C.vio, span: 6, min: 4, src: ['turnover_departement'], description: 'Comparaison des départs d\'employés par département sur la période sélectionnée.' },
  turnover_patterns: { title: 'Prévision turnover', icon: TrendingUp, color: C.coral, span: 8, min: 6, src: ['turnover_patterns'], description: 'Analyse des patterns historiques et alerte sur les périodes à fort turnover.' },
};
const SIZES = [4, 6, 8, 12]; // ⅓ · ½ · ⅔ · pleine largeur
const sizesOf = (id) => SIZES.filter((n) => n >= WIDGETS[id].min);
const ORDER = Object.keys(WIDGETS);
const PRESETS = {
  rh: { label: 'Profil RH', order: ['kpis', 'effectif_evol', 'contrats_repart', 'effectif_bu', 'pyramide_ages', 'cooptation', 'responsabilites', 'masse_activite', 'contrats', 'contrats_expiration', 'masse', 'interim', 'caisse_sociale', 'turnover_evol', 'turnover_motif', 'turnover_departement', 'turnover_patterns', 'logs'] },
  dg: { label: 'Direction générale', order: ['kpis', 'periodes', 'controles', 'interim', 'cout', 'masse', 'contrats_repart', 'effectif_bu', 'pyramide_ages', 'cooptation', 'responsabilites', 'masse_activite', 'contrats_expiration', 'caisse_sociale', 'turnover_evol', 'turnover_motif', 'turnover_departement', 'turnover_patterns', 'logs'] },
  president: { label: 'Président', order: ['kpis', 'periodes', 'interim', 'cout', 'masse', 'controles', 'pyramide_ages', 'cooptation', 'responsabilites', 'masse_activite', 'contrats_expiration', 'caisse_sociale', 'turnover_evol', 'turnover_motif', 'turnover_departement', 'turnover_patterns', 'logs'] },
};
const ETAPES = [
  { label: 'Ouverture de la période', who: 'RH' },
  { label: 'Saisie & calcul de la paie', who: 'RH' },
  { label: 'Contrôle DG des nets par BU', who: 'DG' },
  { label: 'Clôture & code d\'archivage', who: 'Président' },
  { label: 'Fichiers de virement', who: 'Système' },
  { label: 'Contrôle du Président', who: 'Président' },
  { label: 'Ordre de virement', who: 'Président' },
];

/* ═════════ Utilitaires ═════════ */
const NF = new Intl.NumberFormat('fr-FR');
const CF = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 });
const PF = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
// La devise suit la filiale affichée (aucune devise en vue consolidée : les filiales n'ont pas toutes la même)
const makeFmt = (unit) => ({
  unit,
  money: (n) => `${CF.format(n || 0)}${unit ? ` ${unit}` : ''}`,
  full: (n) => `${NF.format(Math.round(n || 0))}${unit ? ` ${unit}` : ''}`,
});
const FmtCtx = createContext(makeFmt(''));
const useFmt = () => useContext(FmtCtx);

const signed = (n, unit) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(Math.round(n * 10) / 10).toLocaleString('fr-FR')} ${unit}`;
const pctChange = (a, b) => (b ? ((a - b) / b) * 100 : null); // null = pas de base de comparaison (et non « 0 % »)
const initials = (s = '') => s.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
const rtf = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
const ago = (d) => {
  const s = (new Date(d) - Date.now()) / 1000; const a = Math.abs(s);
  if (isNaN(s)) return '';
  if (a < 60) return "à l'instant";
  if (a < 3600) return rtf.format(Math.round(s / 60), 'minute');
  if (a < 86400) return rtf.format(Math.round(s / 3600), 'hour');
  return rtf.format(Math.round(s / 86400), 'day');
};
// 'YYYY-MM-DD' → date locale (sans le décalage de fuseau d'un `new Date('YYYY-MM-DD')`)
const dFR = (iso, opts) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString('fr-FR', opts) : '');
const moisLabel = (ym) => { const [y, m] = ym.split('-'); return `${MOIS[Number(m) - 1]} ${y.slice(2)}`; };
const smooth = (pts) => pts.reduce((d, [x, y], i) => {
  if (i === 0) return `M${x},${y}`;
  const [px, py] = pts[i - 1]; const mx = (px + x) / 2;
  return `${d} C${mx},${py} ${mx},${y} ${x},${y}`;
}, '');
const errMsg = (e) => e?.response?.data?.error || e?.message || 'Erreur réseau';
const errDetail = (e) => e?.response?.data?.detail;

// Export CSV (séparateur « ; » + BOM : s'ouvre correctement dans Excel en français)
const downloadCSV = (name, rows) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = `﻿${rows.map((r) => r.map(esc).join(';')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;700;800&family=DM+Sans:wght@400;500;700&display=swap');
.pl-grid { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: 20px; }
.pl-w { grid-column: span 12; min-width: 0; }
@media (min-width: 768px) { .pl-w { grid-column: span var(--span-md); } }
@media (min-width: 1280px) { .pl-w { grid-column: span var(--span); } }
.pl-collapse { display: grid; grid-template-rows: 1fr; transition: grid-template-rows .35s cubic-bezier(.2,.7,.2,1); }
.pl-collapse > div { min-height: 0; overflow: hidden; padding: 4px; margin: -4px; }
.pl-collapse[data-c="1"] { grid-template-rows: 0fr; }
.pl-collapse[data-c="1"] > div { visibility: hidden; transition: visibility 0s .35s; }
.pl-acts { opacity: 1; transition: opacity .2s; }
.pl-card:hover .pl-acts, .pl-card:focus-within .pl-acts { opacity: 1; }
@keyframes pl-spin { to { transform: rotate(360deg); } }
.pl-spin { animation: pl-spin .8s linear infinite; }
.pl-card { background: #fff; border: 1px solid ${C.line}; border-radius: 18px; padding: 20px; box-shadow: 0 1px 2px rgba(11,20,55,.04); transition: transform .25s, box-shadow .25s; }
.pl-card:hover { transform: translateY(-2px); box-shadow: 0 10px 28px -12px rgba(11,20,55,.22); }
.pl-focus:focus-visible { outline: 3px solid ${C.y}; outline-offset: 2px; }
.pl-row { transition: background .15s; }
.pl-row:hover { background: ${C.bg}; }
.pl-th { cursor: pointer; user-select: none; }
.pl-th:hover { color: ${C.ink}; }
@keyframes pl-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
.pl-in { animation: pl-up .55s cubic-bezier(.2,.7,.2,1) backwards; }
@keyframes pl-draw { to { stroke-dashoffset: 0; } }
.pl-draw { stroke-dasharray: 1; stroke-dashoffset: 1; animation: pl-draw 1.2s .15s ease forwards; }
@keyframes pl-fade { from { opacity: 0; } }
.pl-fade { animation: pl-fade .9s .5s both; }
@keyframes pl-grow { from { transform: scaleY(0); } }
.pl-bar { transform-box: fill-box; transform-origin: 50% 100%; animation: pl-grow .7s cubic-bezier(.2,.7,.2,1) both; }
@keyframes pl-dash { from { stroke-dasharray: 0 400; } }
.pl-donut { animation: pl-dash .9s cubic-bezier(.2,.7,.2,1) both; }
@keyframes pl-scale { from { transform: scaleX(0); } }
.pl-fill { transform-origin: left; animation: pl-scale 1.1s .2s cubic-bezier(.2,.7,.2,1) both; }
@keyframes pl-pulse { 0% { transform: scale(1); opacity: .6; } 100% { transform: scale(1.9); opacity: 0; } }
.pl-pulse { animation: pl-pulse 1.8s ease-out infinite; }
@keyframes pl-sk { 50% { opacity: .45; } }
.pl-sk { animation: pl-sk 1.4s ease-in-out infinite; background: ${C.bg}; border-radius: 10px; }
@media (prefers-reduced-motion: reduce) {
  .pl-in, .pl-fade, .pl-bar, .pl-donut, .pl-fill, .pl-pulse, .pl-sk, .pl-spin { animation: none !important; }
  .pl-collapse, .pl-acts, .pl-row { transition: none; }
  .pl-draw { animation: none; stroke-dashoffset: 0; }
  .pl-card { transition: none; }
}
`;

// Mesure la largeur réelle d'un conteneur : les graphiques se redessinent à la bonne taille quand le widget est redimensionné
const useWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    const el = ref.current; if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

/* ═════════ Graphiques SVG légers ═════════ */
const LineChart = ({ labels, series, height = 200, fmt = CF.format }) => {
  const [ref, W] = useWidth();
  const H = height, L = 46, R = 12, T = 14, B = 26;
  const uid = useRef(Math.random().toString(36).slice(2, 7)).current;
  const [hi, setHi] = useState(null);
  const vals = series.flatMap((s) => s.data.filter((v) => v != null));
  if (!vals.length) return <div ref={ref} />;
  const mn = Math.min(...vals), mx = Math.max(...vals), pad = (mx - mn) * 0.18 || mx * 0.1 || 1;
  const lo = Math.max(0, mn - pad), top = mx + pad;
  const x = (i) => L + (i * (W - L - R)) / Math.max(labels.length - 1, 1);
  const y = (v) => T + (1 - (v - lo) / (top - lo)) * (H - T - B);
  const band = (W - L - R) / Math.max(labels.length - 1, 1);
  const step = band < 30 ? 2 : 1;
  return (
    <div ref={ref}><svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }} role="img" onMouseLeave={() => setHi(null)}>
      <defs>
        {series.map((s, i) => (
          <linearGradient key={i} id={`g${uid}${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={s.color} stopOpacity=".28" /><stop offset="1" stopColor={s.color} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
      {[0, 1, 2, 3].map((k) => {
        const v = lo + ((top - lo) * k) / 3;
        return (<g key={k}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={C.line} strokeDasharray="3 5" />
          <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill={C.mut}>{fmt(v)}</text></g>);
      })}
      {labels.map((l, i) => i % step === 0 && (<text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="11" fill={hi === i ? C.ink : C.mut} fontWeight={hi === i ? 700 : 400}>{l}</text>))}
      {series.map((s, si) => {
        const pts = s.data.map((v, i) => (v == null ? null : [x(i), y(v)])).filter(Boolean);
        if (!pts.length) return null;
        const d = smooth(pts);
        return (
          <g key={si}>
            {s.area && <path d={`${d} L${pts[pts.length - 1][0]},${H - B} L${pts[0][0]},${H - B} Z`} fill={`url(#g${uid}${si})`} className="pl-fade" />}
            {s.dashed
              ? <path d={d} fill="none" stroke={s.color} strokeWidth="2" strokeDasharray="5 5" className="pl-fade" />
              : <path d={d} fill="none" stroke={s.color} strokeWidth="3" strokeLinecap="round" pathLength="1" className="pl-draw" />}
          </g>
        );
      })}
      {hi != null && (
        <g pointerEvents="none">
          <line x1={x(hi)} x2={x(hi)} y1={T} y2={H - B} stroke={C.mut} strokeOpacity=".35" />
          {series.map((s, i) => s.data[hi] != null && <circle key={i} cx={x(hi)} cy={y(s.data[hi])} r="5" fill="#fff" stroke={s.color} strokeWidth="3" />)}
          {(() => {
            const rows = series.filter((s) => s.data[hi] != null); const w = 150, h = 22 + rows.length * 16;
            const bx = Math.min(Math.max(x(hi) - w / 2, L), W - R - w);
            return (<g><rect x={bx} y={T - 4} width={w} height={h} rx="9" fill={C.navy} opacity=".94" />
              <text x={bx + 10} y={T + 12} fontSize="11" fill="#A9B4E6">{labels[hi]}</text>
              {rows.map((s, i) => (<text key={i} x={bx + 10} y={T + 28 + i * 16} fontSize="12" fontWeight="700" fill="#fff">{s.name}: {fmt(s.data[hi])}</text>))}</g>);
          })()}
        </g>
      )}
      {labels.map((l, i) => (<rect key={i} x={x(i) - band / 2} y={0} width={band} height={H} fill="transparent" onMouseEnter={() => setHi(i)} />))}
    </svg></div>
  );
};

// Anneau interactif : survol d'un segment OU de sa ligne de légende → mise en avant + valeur au centre
const Donut = ({ data, ariaLabel = 'Répartition des contrats', centerLabel = 'contrats' }) => {
  const [hi, setHi] = useState(null);
  const total = data.reduce((a, d) => a + d.value, 0) || 1;
  const r = 38, c = 2 * Math.PI * r; let acc = 0;
  const cur = hi != null ? data[hi] : null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-5" onMouseLeave={() => setHi(null)}>
      <svg viewBox="0 0 100 100" width="156" height="156" role="img" aria-label={ariaLabel}>
        <circle cx="50" cy="50" r={r} fill="none" stroke={C.bg} strokeWidth="13" />
        {data.map((d, i) => {
          const len = (d.value / total) * c; const off = -acc; acc += len;
          return <circle key={d.label || i} cx="50" cy="50" r={r} fill="none" stroke={PALETTE[i % 5]} strokeWidth={hi === i ? 16 : 13} className="pl-donut"
            strokeDasharray={`${Math.max(len - 1.5, 0)} ${c}`} strokeDashoffset={off} transform="rotate(-90 50 50)"
            style={{ animationDelay: `${i * 110}ms`, opacity: hi == null || hi === i ? 1 : 0.35, transition: 'opacity .2s, stroke-width .2s', cursor: 'pointer' }}
            onMouseEnter={() => setHi(i)} />;
        })}
        <text x="50" y="52" textAnchor="middle" style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 18, fill: C.ink }}>{NF.format(cur ? cur.value : total)}</text>
        <text x="50" y="64" textAnchor="middle" fontSize="7" fill={C.mut}>{cur ? `${cur.label} · ${Math.round((cur.value / total) * 100)} %` : centerLabel}</text>
      </svg>
      <ul className="grid gap-2 text-sm">
        {data.map((d, i) => (
          <li key={d.label || i} className="flex items-center gap-2 px-1.5 -mx-1.5 rounded-lg pl-row" style={{ background: hi === i ? C.bg : undefined }} onMouseEnter={() => setHi(i)}>
            <span className="w-3 h-3 rounded-full" style={{ background: PALETTE[i % 5] }} />
            <span>{d.label || ''}</span><b className="ml-auto pl-3">{NF.format(d.value)}</b>
            <span className="w-10 text-xs text-right" style={{ color: C.mut }}>{Math.round((d.value / total) * 100)} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Bars = ({ data, height = 200, ariaLabel = 'Effectif par BU' }) => {
  const [ref, W] = useWidth();
  const [hi, setHi] = useState(null);
  const H = height, T = 22, B = 30, n = (data || []).length || 1;
  const max = Math.max(...(data || []).map((d) => d.value), 1), bw = W / n, cut = Math.max(4, Math.floor(bw / 7));
  if (!data || data.length === 0) return <div ref={ref} style={{ height: H }} />;
  return (
    <div ref={ref}><svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }} role="img" aria-label={ariaLabel} onMouseLeave={() => setHi(null)}>
      <line x1="0" x2={W} y1={H - B} y2={H - B} stroke={C.line} />
      {data.map((d, i) => {
        const h = (d.value / max) * (H - T - B), bx = i * bw + bw * 0.2, w = bw * 0.6, by = H - B - h;
        return (
          <g key={d.label || i} onMouseEnter={() => setHi(i)} style={{ cursor: 'default' }}>
            <rect x={i * bw} y={0} width={bw} height={H} fill="transparent" />
            <rect className="pl-bar" x={bx} y={by} width={w} height={h} rx="7" fill={d.value === max ? C.y : C.vio} opacity={hi == null || hi === i ? 1 : 0.4}
              style={{ animationDelay: `${i * 70}ms`, transition: 'opacity .2s' }} />
            <text x={bx + w / 2} y={by - 6} textAnchor="middle" fontSize="12" fontWeight="700" fill={C.ink}>{NF.format(d.value)}</text>
            <text x={bx + w / 2} y={H - 10} textAnchor="middle" fontSize="11" fontWeight={hi === i ? 700 : 400} fill={hi === i ? C.ink : C.mut}>
              {hi === i || (d.label || '').length <= cut ? d.label || '' : `${(d.label || '').slice(0, cut - 1)}…`}</text>
          </g>
        );
      })}
    </svg></div>
  );
};

const Spark = ({ data, color }) => {
  const v = (data || []).filter((x) => x != null);
  if (v.length < 2) return <div style={{ height: 28 }} />;
  const mn = Math.min(...v), mx = Math.max(...v);
  const pts = v.map((n, i) => [(i * 100) / (v.length - 1), 25 - ((n - mn) / (mx - mn || 1)) * 22]);
  return (<svg viewBox="0 0 100 28" preserveAspectRatio="none" width="100%" height="28" aria-hidden="true">
    <path d={smooth(pts)} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" pathLength="1" className="pl-draw" vectorEffect="non-scaling-stroke" /></svg>);
};

// Barres empilées par mois (paie normale + intérim) avec info-bulle et part de l'intérim
const StackedBars = ({ items, nameA, nameB, colorA, colorB }) => {
  const { money } = useFmt();
  const [ref, W] = useWidth();
  const [hi, setHi] = useState(null);
  const H = 230, L = 46, R = 8, T = 14, B = 28;
  const max = Math.max(...items.map((i) => i.a + i.b), 1);
  const bw = (W - L - R) / Math.max(items.length, 1);
  const y = (v) => T + (1 - v / max) * (H - T - B);
  const step = bw < 34 ? 2 : 1;
  const cur = hi != null ? items[hi] : null;
  return (
    <div ref={ref} className="relative">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }} role="img" aria-label={`${nameA} et ${nameB} par mois`} onMouseLeave={() => setHi(null)}>
        {[0, 1, 2, 3].map((k) => { const v = (max * k) / 3; return (<g key={k}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={C.line} strokeDasharray="3 5" />
          <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill={C.mut}>{CF.format(v)}</text></g>); })}
        {items.map((it, i) => {
          const bx = L + i * bw + bw * 0.18, w = bw * 0.64, ya = y(it.a), yb = y(it.a + it.b);
          return (
            <g key={it.label} onMouseEnter={() => setHi(i)}>
              <rect x={L + i * bw} y={0} width={bw} height={H} fill="transparent" />
              <rect className="pl-bar" x={bx} y={ya} width={w} height={Math.max(H - B - ya, 0)} rx="4" fill={colorA} opacity={hi == null || hi === i ? 1 : 0.4} style={{ animationDelay: `${i * 40}ms`, transition: 'opacity .2s' }} />
              {it.b > 0 && <rect className="pl-bar" x={bx} y={yb} width={w} height={Math.max(ya - yb, 0)} rx="4" fill={colorB} opacity={hi == null || hi === i ? 1 : 0.4} style={{ animationDelay: `${i * 40 + 120}ms`, transition: 'opacity .2s' }} />}
              {i % step === 0 && <text x={bx + w / 2} y={H - 8} textAnchor="middle" fontSize="11" fontWeight={it.current ? 700 : 400} fill={hi === i || it.current ? C.ink : C.mut}>{it.label}</text>}
            </g>
          );
        })}
      </svg>
      {cur && (
        <div className="absolute px-3 py-2 text-xs text-white pointer-events-none rounded-xl" style={{ top: 0, left: Math.min(Math.max(L + hi * bw - 40, 0), W - 190), background: C.navy, opacity: 0.95, width: 180 }}>
          <div style={{ color: '#A9B4E6' }}>{cur.label}{cur.current ? ' · en cours' : ''}</div>
          <div className="flex items-center gap-1.5 mt-1"><i className="w-2 h-2 rounded-full" style={{ background: colorA }} />{nameA} <b className="ml-auto">{money(cur.a)}</b></div>
          <div className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full" style={{ background: colorB }} />{nameB} <b className="ml-auto">{money(cur.b)}</b></div>
          <div className="mt-1" style={{ color: '#A9B4E6' }}>Part {nameB.toLowerCase()} : <b className="text-white">{PF.format(cur.a + cur.b ? (cur.b / (cur.a + cur.b)) * 100 : 0)} %</b></div>
        </div>
      )}
    </div>
  );
};

// Liste de barres horizontales (une ligne par BU / poste), survol = mise en avant
const HBars = ({ rows, valueKey, prevKey, fmt, color = C.coral }) => {
  const [hi, setHi] = useState(null);
  const max = Math.max(...rows.map((r) => r[valueKey] || 0), 1);
  if (!rows.length) return <Empty>Aucune donnée pour cette période.</Empty>;
  return (
    <ul className="grid gap-2.5 m-0 p-0 list-none" onMouseLeave={() => setHi(null)}>
      {rows.map((r, i) => {
        const v = r[valueKey] || 0, prev = prevKey ? r[prevKey] : 0, d = prev > 0 ? pctChange(v, prev) : null;
        return (
          <li key={r.label} onMouseEnter={() => setHi(i)} className="grid items-center gap-3"
            style={{ gridTemplateColumns: 'minmax(70px, 28%) 1fr auto', opacity: hi == null || hi === i ? 1 : 0.5, transition: 'opacity .2s' }}>
            <span className="text-sm font-bold truncate" title={r.label}>{r.label}</span>
            <div className="h-3 overflow-hidden rounded-full" style={{ background: C.bg }}>
              <div className="h-full rounded-full pl-fill" style={{ width: `${Math.max((v / max) * 100, v > 0 ? 2 : 0)}%`, background: color }} />
            </div>
            <span className="flex items-center gap-2 text-sm font-bold whitespace-nowrap">{fmt(v)}{d != null && <DeltaChip v={d} cost />}</span>
          </li>
        );
      })}
    </ul>
  );
};

// Barre segmentée (net / retenues / charges…) — `denom` permet de comparer deux barres à la même échelle
const SplitBar = ({ parts, fmt, denom, height = 14 }) => {
  const [hi, setHi] = useState(null);
  const sum = parts.reduce((a, p) => a + Math.max(p.value, 0), 0);
  const total = denom || sum || 1;
  return (
    <div onMouseLeave={() => setHi(null)}>
      <div className="flex overflow-hidden rounded-full" style={{ height, background: C.bg }}>
        {parts.map((p, i) => (
          <div key={p.label} className="pl-fill" onMouseEnter={() => setHi(i)} title={`${p.label} · ${fmt(p.value)}`}
            style={{ width: `${(Math.max(p.value, 0) / total) * 100}%`, background: p.color, opacity: hi == null || hi === i ? 1 : 0.4, transition: 'opacity .2s' }} />
        ))}
      </div>
      <ul className="flex flex-wrap m-0 mt-2 p-0 text-xs list-none gap-x-4 gap-y-1">
        {parts.map((p, i) => (
          <li key={p.label} onMouseEnter={() => setHi(i)} className="flex items-center gap-1.5" style={{ color: hi === i ? C.ink : C.mut, fontWeight: hi === i ? 700 : 400 }}>
            <i className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />{p.label}
            <b style={{ color: C.ink }}>{fmt(p.value)}</b>{sum > 0 && <span>· {Math.round((Math.max(p.value, 0) / sum) * 100)} %</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ═════════ Petits composants ═════════ */
const Pill = ({ children, bg = C.bg, fg = C.mut }) => (
  <span className="px-2.5 py-1 text-xs font-bold rounded-full whitespace-nowrap" style={{ background: bg, color: fg }}>{children}</span>
);
const TypePill = ({ type }) => (type === 'interim'
  ? <Pill bg="#FFE9E6" fg="#9A2B1E">Intérim</Pill>
  : <Pill bg="#E4F1FF" fg="#1F5FA8">Normale</Pill>);
const Btn = ({ children, onClick, disabled, tone = 'navy', icon: Icon, title, busy }) => {
  const t = { navy: [C.navy, '#fff'], teal: [C.teal, C.navy], coral: [C.coral, C.navy], ghost: [C.bg, C.ink], yellow: [C.y, C.navy] }[tone];
  const off = disabled || busy;
  return (<button type="button" onClick={onClick} disabled={off} title={title}
    className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold transition rounded-xl pl-focus"
    style={{ background: t[0], color: t[1], opacity: off ? 0.45 : 1, cursor: busy ? 'progress' : off ? 'not-allowed' : 'pointer' }}>
    {busy ? <RefreshCw className="w-4 h-4 pl-spin" /> : Icon && <Icon className="w-4 h-4" />}{children}</button>);
};
const Locked = ({ who }) => (<div className="flex items-center gap-2 px-3 py-2 text-xs rounded-xl" style={{ background: C.bg, color: C.mut }}><Lock className="w-3.5 h-3.5" />Action réservée : {who}</div>);
const Skeleton = () => (<div className="grid gap-3" aria-busy="true"><div className="h-6 pl-sk" style={{ width: '40%' }} /><div className="h-24 pl-sk" /><div className="h-6 pl-sk" style={{ width: '65%' }} /></div>);
const Empty = ({ children }) => (<p className="py-6 text-sm text-center" style={{ color: C.mut }}>{children}</p>);

// Un widget dont la source n'a pas répondu : message clair + « Réessayer » (aucune donnée inventée)
const ErrorBox = ({ errors, keys, onRetry }) => {
  const list = keys.map((k) => errors[k]).filter(Boolean);
  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center" role="alert">
      <span className="flex items-center justify-center w-10 h-10 rounded-full" style={{ background: '#FFE9E6', color: '#9A2B1E' }}><AlertTriangle className="w-5 h-5" /></span>
      <div className="text-sm font-bold">Données indisponibles</div>
      <p className="max-w-sm m-0 text-xs" style={{ color: C.mut }}>{list[0]?.msg || 'La source de données n\'a pas répondu.'}{list[0]?.detail ? ` — ${list[0].detail}` : ''}</p>
      <Btn tone="ghost" icon={RefreshCw} onClick={onRetry}>Réessayer</Btn>
    </div>
  );
};

const Tabs = ({ value, onChange, items, label }) => (
  <div role="tablist" aria-label={label} className="inline-flex flex-wrap gap-0.5 p-1 rounded-xl" style={{ background: C.bg }}>
    {items.map(([k, text, count]) => (
      <button key={k} type="button" role="tab" aria-selected={value === k} onClick={() => onChange(k)}
        className="px-3 py-1.5 text-xs font-bold rounded-lg pl-focus" style={{ background: value === k ? C.navy : 'transparent', color: value === k ? '#fff' : C.navy }}>
        {text}{count != null && <span className="ml-1.5" style={{ opacity: 0.65 }}>{count}</span>}
      </button>
    ))}
  </div>
);

// Variation : `cost` = un coût qui monte n'est pas « mauvais » en soi, on ne colore que les écarts > 5 %
const DeltaChip = ({ v, unit = '%', cost = false }) => {
  if (v == null || !Number.isFinite(v)) return <span className="text-xs" style={{ color: C.mut }}>—</span>;
  const big = Math.abs(v) > 5;
  const [bg, fg] = cost ? (big ? ['#FFF4D6', '#8A6200'] : [C.bg, C.mut]) : (v >= 0 ? ['#E3F7F4', '#0E7C70'] : ['#FFE9E6', '#9A2B1E']);
  const Arrow = v >= 0 ? TrendingUp : TrendingDown;
  return <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-bold rounded-md" style={{ background: bg, color: fg }}><Arrow className="w-3 h-3" />{signed(v, unit)}</span>;
};

const Stat = ({ label, value, sub, delta }) => (
  <div className="p-3 rounded-2xl" style={{ background: C.bg }}>
    <div className="text-xs" style={{ color: C.mut }}>{label}</div>
    <div className="mt-1 text-2xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>{value}</div>
    <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-xs" style={{ color: C.mut }}>
      {delta !== undefined && <DeltaChip v={delta} cost />}{sub}
    </div>
  </div>
);

const ExportBtn = ({ onClick, label = 'CSV' }) => (
  <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg pl-focus" style={{ background: C.bg, color: C.navy }} title="Exporter en CSV (Excel)">
    <Download className="w-3.5 h-3.5" />{label}
  </button>
);

const FOCUSABLE = 'button:not([disabled]),input:not([disabled]),select,textarea,a[href],[tabindex]:not([tabindex="-1"])';
const modalStack = [];
const Modal = ({ title, onClose, children, footer, wide }) => {
  const ref = useRef(null);
  useEffect(() => {
    const prev = document.activeElement, ov = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; modalStack.push(ref);
    ref.current?.querySelector(FOCUSABLE)?.focus();
    return () => { modalStack.splice(modalStack.indexOf(ref), 1); document.body.style.overflow = ov; prev?.focus?.(); };
  }, []);
  useEffect(() => {
    const k = (e) => {
      if (modalStack[modalStack.length - 1] !== ref) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab') {
        const f = [...ref.current.querySelectorAll(FOCUSABLE)]; if (!f.length) return;
        const a = document.activeElement;
        if (e.shiftKey && a === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && a === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  // Portail : le modal reste correct même si le widget parent est « soulevé » au survol
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(11,20,55,.55)' }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} className="w-full p-6 bg-white shadow-2xl rounded-3xl pl-in"
        style={{ maxWidth: wide ? 1100 : 520, maxHeight: '92vh', overflowY: 'auto', fontFamily: BODY, color: C.ink }}>
        <div className="flex items-center mb-4">
          <h3 className="text-xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>{title}</h3>
          <button type="button" onClick={onClose} aria-label="Fermer" className="p-1.5 ml-auto rounded-lg pl-focus"><X className="w-5 h-5" /></button>
        </div>
        {children}
        {footer && <div className="flex justify-end gap-2 mt-5">{footer}</div>}
      </div>
    </div>, document.body);
};

/* ═════════ Widgets métier ═════════ */
const Kpi = ({ icon: Icon, label, value, delta, unit, inverse, spark, color, sub = 'vs mois précédent' }) => {
  const good = delta == null ? null : inverse ? delta <= 0 : delta >= 0;
  const Arrow = delta >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="p-5" style={{ boxShadow: '-1px 0 0 rgba(255,255,255,.1), 0 -1px 0 rgba(255,255,255,.1)' }}>
      <div className="flex items-center gap-3">
        <span className="flex items-center justify-center w-9 h-9 rounded-xl" style={{ background: color, color: C.navy }}><Icon className="w-5 h-5" /></span>
        <span className="text-sm" style={{ color: '#A9B4E6' }}>{label}</span>
      </div>
      <div className="mt-3 text-4xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>{value}</div>
      <div className="flex flex-wrap items-center gap-2 mt-1 text-xs" style={{ color: '#A9B4E6' }}>
        {delta != null && (<span className="inline-flex items-center gap-1 font-bold" style={{ color: good ? '#5FE0D0' : '#FF9E93' }}>
          <Arrow className="w-3.5 h-3.5" />{signed(delta, unit)}</span>)}
        <span>{sub}</span>
      </div>
      <div className="mt-3"><Spark data={spark} color={color} /></div>
    </div>
  );
};

const KpiStrip = ({ rh, masse, effectifs, periodes, interim }) => {
  const { money } = useFmt();
  const k = rh.kpis;
  const eff = effectifs || { total: k.effectif, evolution: pctChange(k.effectif, k.effectif_prec) };

  // Paie en cours : net de toutes les périodes ouvertes (normales + intérim) vs période précédente comparable
  const bu = periodes?.bu || [], buI = periodes?.bu_interim || [];
  const nPer = periodes?.periodes?.length || 0;
  const netEnCours = bu.reduce((a, r) => a + r.net, 0) + buI.reduce((a, r) => a + r.net, 0);
  const cmp = (rows) => ({ net: rows.filter((r) => r.net_prec > 0).reduce((a, r) => a + r.net, 0), prec: rows.reduce((a, r) => a + r.net_prec, 0) });
  const cn = cmp(bu), ci = cmp(buI);
  const dEnCours = cn.prec + ci.prec > 0 ? pctChange(cn.net + ci.net, cn.prec + ci.prec) : null;
  const nApp = (periodes?.periodes || []).filter((p) => p.approuvee).length;

  const hist = interim?.actif ? interim.historique : null;
  return (
    <div className="grid overflow-hidden text-white shadow-lg rounded-3xl"
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', background: `linear-gradient(120deg, ${C.navy}, ${C.navy2} 70%, #22318F)` }}>
      <Kpi icon={Users} label="Effectif total" value={NF.format(eff.total)} delta={eff.evolution} unit="%" spark={rh.effectif_mensuel} color={C.y} />
      <Kpi icon={Wallet} label="Salaire net du mois" value={money(k.masse)} delta={pctChange(k.masse, k.masse_prec)} unit="%" spark={masse.annee} color={C.teal} />
      {periodes && (
        <Kpi icon={ShieldCheck} label="Paie en cours (toutes périodes)" value={money(netEnCours)} delta={dEnCours} unit="%" color={C.sky}
          sub={nPer ? `${nPer} période${nPer > 1 ? 's' : ''} · ${nApp} approuvée${nApp > 1 ? 's' : ''}` : 'aucune période ouverte'}
          spark={hist ? hist.map((h) => h.normal + h.interim) : null} />
      )}
      {interim?.actif && (
        <Kpi icon={Briefcase} label="Paie intérim" value={money(interim.interim.net)} delta={interim.precedent?.net > 0 ? pctChange(interim.interim.net, interim.precedent.net) : null} unit="%" color={C.coral}
          sub={`${NF.format(interim.interim.effectif)} intérimaire${interim.interim.effectif > 1 ? 's' : ''}`} spark={hist ? hist.map((h) => h.interim) : null} />
      )}
      <Kpi icon={UserMinus} label="Turnover (12 mois)" value={`${k.turnover.toLocaleString('fr-FR')} %`} delta={k.turnover - k.turnover_prec} unit="pt" inverse color={C.vio} />
      <Kpi icon={Activity} label="Taux d'absentéisme" value={`${k.absenteisme.toLocaleString('fr-FR')} %`} delta={k.absenteisme - k.absenteisme_prec} unit="pt" inverse color={C.teal} />
    </div>
  );
};

const Masse = ({ data }) => {
  const { money } = useFmt();
  const cur = data.annee || [], prev = data.precedente || [];
  const ytd = cur.reduce((a, v) => a + (v || 0), 0);
  const prevYtd = prev.reduce((a, v, i) => a + (cur[i] != null ? v || 0 : 0), 0);
  const d = pctChange(ytd, prevYtd);
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="text-3xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>{money(ytd)}</div>
        {d != null && <Pill bg={d >= 0 ? '#E3F7F4' : '#FFE9E6'} fg={d >= 0 ? '#0E7C70' : '#9A2B1E'}>{signed(d, '%')} vs {YEAR - 1}</Pill>}
        <div className="flex gap-4 ml-auto text-xs" style={{ color: C.mut }}>
          <span className="flex items-center gap-1.5"><i className="w-3 h-1 rounded" style={{ background: C.teal }} />{YEAR}</span>
          <span className="flex items-center gap-1.5"><i className="w-3 h-0 border-t-2 border-dashed" style={{ borderColor: C.mut }} />{YEAR - 1}</span>
        </div>
      </div>
      <LineChart labels={MOIS} series={[{ name: String(YEAR - 1), data: prev, color: C.mut, dashed: true }, { name: String(YEAR), data: cur, color: C.teal, area: true }]} />
    </>
  );
};

const Timeline = ({ data }) => {
  const cur = data.etape; // 1..7 = étape en cours · 8 = processus terminé
  const pct = Math.min(1, (cur - 1) / (ETAPES.length - 1));
  const enCours = ETAPES[cur - 1];
  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Pill bg="#EEEBFE" fg="#5B4BD0">{data.periode}</Pill>
        {enCours
          ? <span className="text-sm" style={{ color: C.mut }}>Étape {cur}/{ETAPES.length} · en attente de <b style={{ color: C.ink }}>{enCours.who}</b></span>
          : <Pill bg="#E3F7F4" fg="#0E7C70">Processus terminé · ordre de virement émis</Pill>}
      </div>
      <div className="overflow-x-auto">
        <div className="relative" style={{ minWidth: 780 }}>
          <div className="absolute h-1 rounded-full" style={{ left: '7.14%', right: '7.14%', top: 14, background: C.line }}>
            <div className="h-full rounded-full pl-fill" style={{ width: `${pct * 100}%`, background: `linear-gradient(90deg, ${C.teal}, ${C.y})` }} />
          </div>
          <ol className="relative grid p-0 m-0 list-none" style={{ gridTemplateColumns: `repeat(${ETAPES.length}, 1fr)` }}>
            {ETAPES.map((e, i) => {
              const done = i + 1 < cur, active = i + 1 === cur, info = data.etapes?.[i];
              return (
                <li key={e.label} className="flex flex-col items-center px-1 text-center">
                  <span className="relative flex items-center justify-center text-xs font-bold rounded-full w-7 h-7"
                    style={{ background: done ? C.teal : active ? C.y : '#fff', color: C.navy, border: `2px solid ${done ? C.teal : active ? C.y : C.line}` }}>
                    {active && <span className="absolute inset-0 rounded-full pl-pulse" style={{ background: C.y }} />}
                    <span className="relative">{done ? <Check className="w-4 h-4" /> : i + 1}</span>
                  </span>
                  <span className="mt-2 text-xs font-bold leading-tight" style={{ color: active || done ? C.ink : C.mut }}>{e.label}</span>
                  <span className="mt-0.5 text-xs" style={{ color: C.mut }}>{info?.date || e.who}</span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </>
  );
};

const Contrats = ({ data, can, onAct }) => {
  const [page, setPage] = useState(0);
  const pageSize = 5;
  const totalPages = Math.ceil(data.length / pageSize);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0)); // après une validation, la dernière page peut disparaître
  const currentPageData = data.slice(safePage * pageSize, (safePage + 1) * pageSize);

  if (data.length === 0) return <Empty>Aucun contrat en attente de validation 🎉</Empty>;

  return (
    <>
      {can && (
        <div className="flex gap-2 mb-3">
          <button type="button" onClick={() => onAct(null, 'valider-tout')} className="px-3 py-1.5 text-xs font-bold rounded-lg pl-focus" style={{ background: C.teal, color: C.navy }}>
            Tout valider
          </button>
          <button type="button" onClick={() => onAct(null, 'rejeter-tout')} className="px-3 py-1.5 text-xs font-bold rounded-lg pl-focus" style={{ background: '#FFE9E6', color: '#9A2B1E' }}>
            Tout rejeter
          </button>
        </div>
      )}
      <ul className="grid gap-2 p-0 m-0 list-none">
        {currentPageData.map((c, i) => (
          <li key={c.idpersonnel_contrat} className="flex items-center gap-3 p-2 rounded-xl" style={{ background: i % 2 ? 'transparent' : C.bg }}>
            <a href={`/contrats/${c.personnel_id}`} className="text-xs font-bold pl-focus" style={{ color: C.navy, textDecoration: 'none' }}>
              Voir détails
            </a>
            <span className="flex items-center justify-center flex-none w-10 h-10 text-sm font-bold rounded-full" style={{ background: PALETTE[i % 5], color: C.navy }}>{initials(`${c.prenom} ${c.nom}`)}</span>
            <div className="min-w-0">
              <div className="font-bold truncate">{c.prenom} {c.nom}</div>
              <div className="text-xs truncate" style={{ color: C.mut }}>{c.libtitre} · dès le {new Date(c.date_entree_contrat).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</div>
            </div>
            <Pill bg="#EEEBFE" fg="#5B4BD0">{c.libcontrat}</Pill>
            {can ? (
              <div className="flex gap-1.5 ml-auto">
                <button type="button" aria-label={`Valider le contrat de ${c.prenom} ${c.nom}`} onClick={() => onAct(c, 'valider')} className="flex items-center justify-center rounded-xl w-9 h-9 pl-focus" style={{ background: C.teal, color: C.navy }}><Check className="w-4 h-4" /></button>
                <button type="button" aria-label={`Rejeter le contrat de ${c.prenom} ${c.nom}`} onClick={() => onAct(c, 'rejeter')} className="flex items-center justify-center rounded-xl w-9 h-9 pl-focus" style={{ background: '#FFE9E6', color: '#9A2B1E' }}><X className="w-4 h-4" /></button>
              </div>
            ) : <Lock className="w-4 h-4 ml-auto" style={{ color: C.mut }} />}
          </li>
        ))}
      </ul>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <button type="button" onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage === 0} className="px-3 py-1 text-xs font-bold rounded-lg pl-focus" style={{ background: safePage === 0 ? C.line : '#fff', color: C.navy }}>
            Précédent
          </button>
          <span className="text-xs" style={{ color: C.mut }}>
            {safePage + 1} / {totalPages}
          </span>
          <button type="button" onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))} disabled={safePage === totalPages - 1} className="px-3 py-1 text-xs font-bold rounded-lg pl-focus" style={{ background: safePage === totalPages - 1 ? C.line : '#fff', color: C.navy }}>
            Suivant
          </button>
        </div>
      )}
    </>
  );
};

const ContratsExpiration = ({ data }) => {
  const [jours, setJours] = useState(30); // Valeur par défaut : 30 jours
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localData, setLocalData] = useState(data);
  const { filialeId, isConsolidated } = useFiliale();

  // Charger les données quand le paramètre jours change
  useEffect(() => {
    const fetchContratsExpiration = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { jours };
        if (!isConsolidated && filialeId && filialeId !== 'all') {
          params.filiale_id = filialeId;
        }
        const response = await http.get('/api/contrats-validation/expiration', { params });
        setLocalData(response.data);
      } catch (err) {
        setError(err.message || 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    fetchContratsExpiration();
  }, [jours, filialeId, isConsolidated]);

  // Mettre à jour localData quand data change (reçu du parent)
  useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  const handleJoursChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value >= 1 && value <= 365) {
      setJours(value);
    }
  };

  const contrats = localData?.contrats || [];
  const count = localData?.count || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 pl-spin" style={{ color: C.mut }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center" style={{ color: C.coral }}>
        <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
        <div className="text-sm">{error}</div>
        <button type="button" onClick={() => setJours(jours)} className="mt-2 text-xs font-bold underline">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-end gap-3 mb-4">
        <div>
          <div className="text-xs" style={{ color: C.mut }}>Contrats arrivant à expiration</div>
          <div className="text-2xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>
            {NF.format(count)}
          </div>
        </div>
        <Pill bg="#FFE9E6" fg="#9A2B1E">{jours} jours</Pill>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <label htmlFor="jours-expiration" className="text-xs" style={{ color: C.mut }}>
          Horizon (jours) :
        </label>
        <input
          id="jours-expiration"
          type="number"
          min="1"
          max="365"
          value={jours}
          onChange={handleJoursChange}
          className="w-20 px-2 py-1 text-sm font-bold rounded-lg border pl-focus"
          style={{ background: '#fff', color: C.navy, borderColor: C.line }}
        />
        <span className="text-xs" style={{ color: C.mut }}>(1-365)</span>
      </div>

      {contrats.length === 0 ? (
        <Empty>Aucun contrat arrivant à expiration dans {jours} jours 🎉</Empty>
      ) : (
        <ul className="grid gap-2 p-0 m-0 max-h-64 overflow-y-auto">
          {contrats.map((c) => {
            const dateFin = new Date(c.date_fin);
            const joursRestants = Math.ceil((dateFin - new Date()) / (1000 * 60 * 60 * 24));
            const isUrgent = joursRestants <= 7;
            const isWarning = joursRestants <= 30 && joursRestants > 7;

            return (
              <li key={c.id} className="flex items-center gap-3 p-3 rounded-xl pl-row" style={{ background: C.bg }}>
                <AlertTriangle className={`flex-none w-5 h-5 ${isUrgent ? 'text-[#FF7A6B]' : isWarning ? 'text-[#FFC93C]' : 'text-gray-400'}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold truncate">{c.prenom} {c.nom}</div>
                  <div className="text-xs" style={{ color: C.mut }}>
                    {c.poste} · {c.type_contrat}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold" style={{ color: isUrgent ? '#FF7A6B' : isWarning ? '#FFC93C' : C.mut }}>
                    {joursRestants > 0 ? `${joursRestants} jour${joursRestants > 1 ? 's' : ''}` : 'Expiré'}
                  </div>
                  <div className="text-xs" style={{ color: C.mut }}>
                    {dFR(c.date_fin, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

const PyramideAges = ({ data }) => {
  const tranches = data?.tranches || [];
  const total = data?.total || 0;

  // Tranches d'âge standard
  const TRANCHES_LABELS = ['< 25', '25-29', '30-34', '35-39', '40-44', '45-49', '50-54', '55+'];

  if (tranches.length === 0) {
    return <Empty>Aucune donnée d'âge disponible</Empty>;
  }

  return (
    <>
      <div className="flex items-end gap-3 mb-4">
        <div>
          <div className="text-xs" style={{ color: C.mut }}>Pyramide des âges</div>
          <div className="text-2xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>
            {NF.format(total)}
          </div>
        </div>
        <Pill bg="#EEEBFE" fg="#5B4BD0">{total} collaborateurs</Pill>
      </div>

      <div className="space-y-2">
        {tranches.map((t, i) => {
          const hommes = t.hommes || 0;
          const femmes = t.femmes || 0;
          const totalTranche = hommes + femmes;
          const pctH = totalTranche > 0 ? (hommes / totalTranche) * 100 : 0;
          const pctF = totalTranche > 0 ? (femmes / totalTranche) * 100 : 0;
          const label = TRANCHES_LABELS[i] || `${t.min}-${t.max}`;

          return (
            <div key={i} className="flex items-center gap-2">
              <div className="w-12 text-xs" style={{ color: C.mut }}>{label}</div>
              <div className="flex-1 h-4 flex gap-0.5 rounded overflow-hidden" style={{ background: C.bg }}>
                <div
                  className="h-full rounded-l"
                  style={{
                    width: `${pctH}%`,
                    background: C.sky,
                    minWidth: hommes > 0 ? '2px' : '0'
                  }}
                  title={`${hommes} hommes (${PF.format(pctH)}%)`}
                />
                <div
                  className="h-full rounded-r"
                  style={{
                    width: `${pctF}%`,
                    background: C.vio,
                    minWidth: femmes > 0 ? '2px' : '0'
                  }}
                  title={`${femmes} femmes (${PF.format(pctF)}%)`}
                />
              </div>
              <div className="w-20 text-right text-xs" style={{ color: C.mut }}>
                {totalTranche > 0 ? `${PF.format(pctH)}% H / ${PF.format(pctF)}% F` : '-'}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t" style={{ borderColor: C.line }}>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ background: C.sky }} />
          <span className="text-xs" style={{ color: C.mut }}>Hommes</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ background: C.vio }} />
          <span className="text-xs" style={{ color: C.mut }}>Femmes</span>
        </div>
      </div>
    </>
  );
};

const Cooptation = ({ data }) => {
  const taux = data?.taux || 0;
  const total = data?.total || 0;
  const cooptes = data?.cooptes || 0;

  if (total === 0) {
    return <Empty>Aucune donnée de recrutement disponible</Empty>;
  }

  return (
    <>
      <div className="flex items-end gap-3 mb-4">
        <div>
          <div className="text-xs" style={{ color: C.mut }}>Taux de cooptation</div>
          <div className="text-2xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>
            {PF.format(taux)}%
          </div>
        </div>
        <Pill bg={taux >= 30 ? '#E3F7F4' : taux >= 20 ? '#FFF4D6' : '#FFE9E6'} fg={taux >= 30 ? '#0E7C70' : taux >= 20 ? '#8A6200' : '#9A2B1E'}>
          {taux >= 30 ? 'Excellent' : taux >= 20 ? 'Bon' : 'À améliorer'}
        </Pill>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: C.bg }}>
          <div>
            <div className="text-sm font-bold">Recrutements totaux</div>
            <div className="text-xs" style={{ color: C.mut }}>Sur la période sélectionnée</div>
          </div>
          <div className="text-xl font-bold" style={{ color: C.ink }}>{NF.format(total)}</div>
        </div>

        <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: '#E3F7F4' }}>
          <div>
            <div className="text-sm font-bold" style={{ color: '#0E7C70' }}>Via cooptation</div>
            <div className="text-xs" style={{ color: '#0E7C70', opacity: 0.7 }}>Recommandation par salariés</div>
          </div>
          <div className="text-xl font-bold" style={{ color: '#0E7C70' }}>{NF.format(cooptes)}</div>
        </div>

        <div className="h-3 overflow-hidden rounded-full" style={{ background: C.line }}>
          <div
            className="h-full rounded-full pl-fill"
            style={{
              width: `${Math.min(taux, 100)}%`,
              background: taux >= 30 ? C.teal : taux >= 20 ? C.y : C.coral
            }}
          />
        </div>

        <div className="text-xs text-center" style={{ color: C.mut }}>
          {cooptes} cooptation{cooptes > 1 ? 's' : ''} sur {total} recrutement{total > 1 ? 's' : ''}
        </div>
      </div>
    </>
  );
};

const Responsabilites = ({ data }) => {
  const moyenne = data?.moyenne || 0;
  const managers = data?.managers || [];
  const totalManagers = data?.total_managers || 0;

  if (totalManagers === 0) {
    return <Empty>Aucun manager avec subordonnés directs</Empty>;
  }

  return (
    <>
      <div className="flex items-end gap-3 mb-4">
        <div>
          <div className="text-xs" style={{ color: C.mut }}>Responsabilités</div>
          <div className="text-2xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>
            {PF.format(moyenne)}
          </div>
        </div>
        <Pill bg={moyenne >= 10 ? '#E3F7F4' : moyenne >= 6 ? '#FFF4D6' : '#FFE9E6'} fg={moyenne >= 10 ? '#0E7C70' : moyenne >= 6 ? '#8A6200' : '#9A2B1E'}>
          {moyenne >= 10 ? 'Optimal' : moyenne >= 6 ? 'Acceptable' : 'Surcharge'}
        </Pill>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: C.bg }}>
          <div>
            <div className="text-sm font-bold">Nombre moyen de subordonnés</div>
            <div className="text-xs" style={{ color: C.mut }}>Par manager (subordonnés directs)</div>
          </div>
          <div className="text-xl font-bold" style={{ color: C.ink }}>{PF.format(moyenne)}</div>
        </div>

        <div className="h-3 overflow-hidden rounded-full" style={{ background: C.line }}>
          <div
            className="h-full rounded-full pl-fill"
            style={{
              width: `${Math.min((moyenne / 15) * 100, 100)}%`,
              background: moyenne >= 10 ? C.teal : moyenne >= 6 ? C.y : C.coral
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-xl text-center" style={{ background: '#E4F1FF' }}>
            <div className="text-2xl font-bold" style={{ color: '#1F5FA8' }}>{NF.format(totalManagers)}</div>
            <div className="text-xs" style={{ color: '#1F5FA8', opacity: 0.7 }}>Managers</div>
          </div>
          <div className="p-3 rounded-xl text-center" style={{ background: '#E3F7F4' }}>
            <div className="text-2xl font-bold" style={{ color: '#0E7C70' }}>{NF.format(data?.total_subordonnes || 0)}</div>
            <div className="text-xs" style={{ color: '#0E7C70', opacity: 0.7 }}>Subordonnés</div>
          </div>
        </div>

        {managers.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-bold mb-2" style={{ color: C.mut }}>Top 5 des managers les plus chargés</div>
            <ul className="space-y-2">
              {managers.slice(0, 5).map((m, i) => (
                <li key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: C.bg }}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold truncate">{m.prenom} {m.nom}</div>
                    <div className="text-xs" style={{ color: C.mut }}>{m.poste || 'Non défini'}</div>
                  </div>
                  <div className="text-sm font-bold" style={{ color: C.ink }}>{NF.format(m.subordonnes)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

const MasseActivite = ({ data, filialeCode, devise }) => {
  const activites = data?.activites || [];
  const totalMasse = data?.total_masse || 0;
  const chargesPatronales = data?.charges_patronales || [];
  const { money } = useFmt();

  if (activites.length === 0) {
    return <Empty>Aucune activité avec masse salariale</Empty>;
  }

  // Préparer les données pour le graphique donut
  const chartData = activites.map((a, i) => ({
    label: a.Libelle_activite,
    value: a.masse,
    color: ['#FFC93C', '#25B8A8', '#8B7CF6', '#4EA8FF', '#FF7A6B', '#0B1437'][i % 6]
  }));

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-end gap-3">
          <div>
            <div className="text-xs" style={{ color: C.mut }}>Masse salariale totale</div>
            <div className="text-2xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>
              {money(totalMasse)}
            </div>
          </div>
          {filialeCode && (
            <Pill bg="#E3F7F4" fg="#0E7C70">{filialeCode}</Pill>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Réutiliser le composant Donut */}
          <Donut data={chartData} ariaLabel="Répartition de la masse salariale par activité" centerLabel="activité" />

          {/* Légende */}
          <div className="space-y-2">
            {chartData.map((d, i) => {
              const pourcentage = totalMasse > 0 ? (d.value / totalMasse) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{ background: d.color }} />
                  <span className="flex-1 truncate" style={{ color: C.ink }}>{d.label}</span>
                  <span className="font-bold tabular-nums" style={{ color: C.ink }}>{PF.format(pourcentage)}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {chargesPatronales.length > 0 && (
          <div className="mt-4 p-3 rounded-xl" style={{ background: C.bg }}>
            <div className="text-xs font-bold mb-2" style={{ color: C.mut }}>Charges patronales incluses</div>
            <div className="flex flex-wrap gap-2">
              {chargesPatronales.map((charge, i) => (
                <span key={i} className="px-2 py-1 text-xs rounded-lg bg-white" style={{ color: C.ink }}>
                  {charge}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const CaisseSociale = ({ data, devise }) => {
  const { money, full } = useFmt();
  const montantActuel = data?.montant_actuel || 0;
  const montantPrecedent = data?.montant_precedent || 0;
  // Null si pas de mois précédent (pour éviter d'afficher 0,0% vert)
  const variation = montantPrecedent > 0 ? ((montantActuel - montantPrecedent) / montantPrecedent) * 100 : null;
  const isPositive = variation != null && variation >= 0;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-bold" style={{ color: C.ink }}>Montant actuel</div>
        <div className="text-2xl font-bold tabular-nums" style={{ color: C.ink }}>{money(montantActuel)}</div>
      </div>

      <div className="flex items-center justify-between mb-4 p-3 rounded-xl" style={{ background: C.bg }}>
        <div>
          <div className="text-xs" style={{ color: C.mut }}>Mois précédent</div>
          <div className="text-lg font-bold tabular-nums" style={{ color: C.ink }}>{money(montantPrecedent)}</div>
        </div>
        {variation != null ? (
          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg ${isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span className="text-sm font-bold tabular-nums">{Math.abs(variation).toFixed(1)}%</span>
          </div>
        ) : (
          <div className="px-3 py-1.5 rounded-lg text-sm font-bold" style={{ background: C.bg, color: C.mut }}>
            N/A
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm">
        <span style={{ color: C.mut }}>Variation absolue</span>
        {variation != null ? (
          <span className={`font-bold tabular-nums ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '+' : ''}{full(montantActuel - montantPrecedent)}
          </span>
        ) : (
          <span className="font-bold tabular-nums" style={{ color: C.mut }}>N/A</span>
        )}
      </div>
    </>
  );
};

const Virements = ({ data, canControl, canOrder, onControl, onOrder }) => {
  const { money, full } = useFmt();
  const [ask, setAsk] = useState(false);
  const f = data.fichiers || [];
  const ok = f.filter((x) => x.statut === 'controle').length;
  const total = f.reduce((a, x) => a + x.montant, 0);
  const ready = f.length > 0 && ok === f.length && !data.ordre;
  return (
    <>
      <div className="flex items-end gap-3 mb-3">
        <div><div className="text-xs" style={{ color: C.mut }}>{data.periode}</div>
          <div className="text-2xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>{money(total)}</div></div>
        <Pill bg={ok === f.length && f.length ? '#E3F7F4' : '#FFF4D6'} fg={ok === f.length && f.length ? '#0E7C70' : '#8A6200'}>{ok}/{f.length} contrôlés</Pill>
      </div>
      <div className="h-2 mb-4 overflow-hidden rounded-full" style={{ background: C.line }}>
        <div className="h-full rounded-full pl-fill" style={{ width: `${f.length ? (ok / f.length) * 100 : 0}%`, background: C.teal }} />
      </div>
      {f.length === 0 && <Empty>Aucun fichier de virement pour cette période.</Empty>}
      <ul className="grid gap-2 p-0 m-0 mb-4 list-none">
        {f.map((x) => {
          const c = x.statut === 'controle';
          return (
            <li key={x.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: C.bg }}>
              <Landmark className="flex-none w-5 h-5" style={{ color: C.coral }} />
              <div className="min-w-0"><div className="text-sm font-bold truncate">{x.banque}</div>
                <div className="text-xs" style={{ color: C.mut }}>{NF.format(x.nb)} bénéficiaires · {money(x.montant)}</div></div>
              {canControl && !data.ordre ? (
                <button type="button" onClick={() => onControl(x, !c)} aria-pressed={c} className="px-3 py-1.5 ml-auto text-xs font-bold rounded-lg pl-focus"
                  style={{ background: c ? C.teal : '#fff', color: C.navy, border: `1px solid ${c ? C.teal : C.line}` }}>{c ? '✓ Contrôlé' : 'Contrôler'}</button>
              ) : <span className="ml-auto"><Pill bg={c ? '#E3F7F4' : '#FFF4D6'} fg={c ? '#0E7C70' : '#8A6200'}>{c ? 'Contrôlé' : 'À contrôler'}</Pill></span>}
            </li>
          );
        })}
      </ul>
      {data.ordre ? (
        <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: '#E3F7F4', color: '#0E7C70' }}>
          <Check className="w-5 h-5" /><div className="text-sm"><b>Ordre de virement émis</b><div className="text-xs">Réf. {data.ordre.reference} · {new Date(data.ordre.date).toLocaleDateString('fr-FR')}</div></div>
        </div>
      ) : canOrder ? (
        <Btn tone="yellow" icon={Landmark} disabled={!ready} onClick={() => setAsk(true)} title={ready ? '' : 'Tous les fichiers doivent être contrôlés'}>Générer l'ordre de virement</Btn>
      ) : <Locked who="Président" />}
      {ask && (
        <Modal title="Générer l'ordre de virement" onClose={() => setAsk(false)} footer={<>
          <Btn tone="ghost" onClick={() => setAsk(false)}>Annuler</Btn>
          <Btn tone="yellow" icon={Check} onClick={() => { setAsk(false); onOrder(); }}>Confirmer l'ordre</Btn></>}>
          <p className="text-sm leading-relaxed" style={{ color: C.mut }}>
            Vous allez émettre l'ordre de virement de <b style={{ color: C.ink }}>{full(total)}</b> pour {f.length} fichier{f.length > 1 ? 's' : ''}.
            Cette action constitue la <b style={{ color: C.ink }}>dernière étape</b> du processus de paie et n'est pas réversible.
          </p>
        </Modal>
      )}
    </>
  );
};

/* ───────── Validation des périodes : normale + intérim, sélection, tableau BU triable ───────── */
const keyOf = (p) => `${p.type === 'interim' ? 'interim' : 'normal'}:${p.id}`;
const archiveName = (p) => `${p.type === 'interim' ? 'paie_element_interim' : 'paie_element'}_${String(p.date_debut || '').replace(/-/g, '_')}`;

const Periodes = ({ data, controles, can, busy, onApprouver, onCloturer, isConsolidated, filialeLabel }) => {
  const { full } = useFmt();
  const [tab, setTab] = useState('tout');          // 'tout' | 'normal' | 'interim'
  const [off, setOff] = useState(() => new Set());  // périodes décochées
  const [ask, setAsk] = useState(null);             // 'approuver' | 'cloturer' | null
  const [sure, setSure] = useState(false);
  const [sureAnom, setSureAnom] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [sort, setSort] = useState({ key: 'bu', dir: 1 });
  const [q, setQ] = useState('');

  const periodes = data.periodes || [];
  const nI = periodes.filter((p) => p.type === 'interim').length;
  const matchTab = (p) => tab === 'tout' || (tab === 'interim' ? p.type === 'interim' : p.type !== 'interim');
  const visibles = periodes.filter(matchTab);
  const sel = visibles.filter((p) => !off.has(keyOf(p)));        // les actions portent sur ce qui est visible ET coché
  const aApprouver = sel.filter((p) => !p.approuvee);
  const aCloturer = sel.filter((p) => p.approuvee);
  const netApprouver = aApprouver.reduce((a, p) => a + (p.net || 0), 0);
  const netCloturer = aCloturer.reduce((a, p) => a + (p.net || 0), 0);
  const crit = controles?.critiques || 0;

  const parFiliale = useMemo(() => {
    const m = new Map();
    visibles.forEach((p) => {
      const k = p.filiale_id ?? 0;
      if (!m.has(k)) m.set(k, { id: k, libelle: p.filiale_libelle || `Filiale ${k}`, items: [] });
      m.get(k).items.push(p);
    });
    return [...m.values()];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodes, tab]);
  const multiFiliale = parFiliale.length > 1;

  // Tableau BU : normale et intérim fusionnés sur la BU, colonnes selon l'onglet
  const showN = tab !== 'interim', showI = tab !== 'normal';
  const rows = useMemo(() => {
    const m = new Map();
    const blank = (bu) => ({ bu, effN: 0, netN: 0, precN: 0, effI: 0, netI: 0, precI: 0 });
    // Clé unique combinant bu et idbusiness_unit pour éviter l'écrasement si plusieurs filiales ont des BU du même nom
    const key = (r) => `${r.bu}|${r.idbusiness_unit || 0}`;
    (data.bu || []).forEach((r) => m.set(key(r), { ...blank(r.bu), effN: r.effectif, netN: r.net, precN: r.net_prec || 0 }));
    (data.bu_interim || []).forEach((r) => { const k = key(r); const x = m.get(k) || blank(r.bu); Object.assign(x, { effI: r.effectif, netI: r.net, precI: r.net_prec || 0 }); m.set(k, x); });
    return [...m.values()].map((x) => {
      const useN = showN && x.precN > 0, useI = showI && x.precI > 0;   // on ne compare que ce qui a une base de comparaison
      const cmpNet = (useN ? x.netN : 0) + (useI ? x.netI : 0), cmpPrec = (useN ? x.precN : 0) + (useI ? x.precI : 0);
      return { ...x, eff: (showN ? x.effN : 0) + (showI ? x.effI : 0), net: (showN ? x.netN : 0) + (showI ? x.netI : 0), cmpNet, cmpPrec, delta: cmpPrec > 0 ? pctChange(cmpNet, cmpPrec) : null };
    }).filter((x) => x.eff > 0 || x.net > 0);
  }, [data, showN, showI]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = rows.filter((r) => !needle || r.bu.toLowerCase().includes(needle));
    const v = (r) => (sort.key === 'bu' ? r.bu.toLowerCase() : r[sort.key] ?? -Infinity);
    return [...list].sort((a, b) => (v(a) < v(b) ? -sort.dir : v(a) > v(b) ? sort.dir : 0));
  }, [rows, q, sort]);
  const tot = shown.reduce((a, r) => ({ eff: a.eff + r.eff, effN: a.effN + r.effN, effI: a.effI + r.effI, net: a.net + r.net, netN: a.netN + r.netN, netI: a.netI + r.netI, cmpNet: a.cmpNet + r.cmpNet, cmpPrec: a.cmpPrec + r.cmpPrec }),
    { eff: 0, effN: 0, effI: 0, net: 0, netN: 0, netI: 0, cmpNet: 0, cmpPrec: 0 });
  const maxNet = Math.max(...shown.map((r) => r.net), 1);

  const sortBy = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === 'bu' ? 1 : -1 }));
  const Th = ({ k, children, right = true }) => (
    <th className={`pb-2 font-medium pl-th ${right ? 'text-right' : ''}`} onClick={() => sortBy(k)} aria-sort={sort.key === k ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown className="w-3 h-3" style={{ opacity: sort.key === k ? 1 : 0.35 }} /></span>
    </th>
  );

  const toggleOff = (p) => setOff((s) => { const n = new Set(s); const k = keyOf(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const allChecked = visibles.length > 0 && visibles.every((p) => !off.has(keyOf(p)));
  const toggleAll = () => setOff((s) => { const n = new Set(s); visibles.forEach((p) => (allChecked ? n.add(keyOf(p)) : n.delete(keyOf(p)))); return n; });
  const copy = async (txt, key) => {
    try { await navigator.clipboard.writeText(txt); setCopiedKey(key); setTimeout(() => setCopiedKey(null), 1800); } catch { /* silencieux */ }
  };
  const close = () => { setAsk(null); setSure(false); setSureAnom(false); };
  const exportRows = () => downloadCSV(`net-par-bu-${tab}.csv`, [
    ['BU', 'Effectif', ...(showN ? ['Net normal'] : []), ...(showI ? ['Net intérim'] : []), 'Net total', 'Variation vs période précédente (%)'],
    ...shown.map((r) => [r.bu, r.eff, ...(showN ? [Math.round(r.netN)] : []), ...(showI ? [Math.round(r.netI)] : []), Math.round(r.net), r.delta == null ? '' : Math.round(r.delta * 10) / 10]),
    ['Total', tot.eff, ...(showN ? [Math.round(tot.netN)] : []), ...(showI ? [Math.round(tot.netI)] : []), Math.round(tot.net), tot.cmpPrec > 0 ? Math.round(pctChange(tot.cmpNet, tot.cmpPrec) * 10) / 10 : ''],
  ]);

  return (
    <>
      {/* Bandeau de périmètre + filtre de type */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Pill bg={isConsolidated ? '#EEEBFE' : '#E3F7F4'} fg={isConsolidated ? '#5B4BD0' : '#0E7C70'}>
          {isConsolidated ? 'Périmètre consolidé · toutes les filiales' : `Filiale · ${filialeLabel || '—'}`}
        </Pill>
        <div className="ml-auto">
          <Tabs value={tab} onChange={setTab} label="Type de paie"
            items={[['tout', 'Tout', periodes.length], ['normal', 'Normale', periodes.length - nI], ['interim', 'Intérim', nI]]} />
        </div>
      </div>

      {data.interim_error && (
        <div className="flex items-start gap-2 p-3 mb-4 text-xs rounded-xl" role="alert" style={{ background: '#FFF4D6', color: '#8A6200' }}>
          <AlertTriangle className="flex-none w-4 h-4 mt-0.5" />
          <span>Les périodes <b>intérim</b> n'ont pas pu être lues ({data.interim_error}). Vérifiez que la migration de <code>paie_interime</code> a été exécutée.</span>
        </div>
      )}

      {/* Liste des périodes, groupées par filiale — cochez celles à traiter */}
      {visibles.length === 0 ? (
        <div className="mb-4">
          <Empty>Aucune période {tab === 'tout' ? 'ouverte' : tab === 'interim' ? 'intérim ouverte' : 'normale ouverte'} actuellement.</Empty>
          {data.cloture?.code && (
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs" style={{ color: C.mut }}>
              Dernier code de clôture <b style={{ color: C.ink }}>{data.cloture.code}</b> ({dFR(data.cloture.date)})
              <button type="button" onClick={() => copy(data.cloture.code, 'cloture')} className="inline-flex items-center gap-1 px-2 py-0.5 font-bold rounded-lg pl-focus" style={{ background: C.bg }}>
                {copiedKey === 'cloture' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{copiedKey === 'cloture' ? 'Copié' : 'Copier'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="flex items-center gap-2 text-xs font-bold cursor-pointer" style={{ color: C.mut }}>
              <input type="checkbox" checked={allChecked} onChange={toggleAll} />
              {sel.length}/{visibles.length} sélectionnée{sel.length > 1 ? 's' : ''}
            </label>
          </div>
          <ul className="grid gap-3 p-0 m-0 list-none">
            {parFiliale.map((f) => (
              <li key={f.id} className="p-3 rounded-xl" style={{ background: C.bg }}>
                <div className="flex items-center gap-2 mb-2">
                  <Landmark className="w-4 h-4" style={{ color: C.navy2 }} />
                  <b className="text-sm">{f.libelle}</b>
                  <Pill bg="#fff" fg={C.mut}>{f.items.length} période{f.items.length > 1 ? 's' : ''}</Pill>
                </div>
                <ul className="grid gap-1.5 p-0 m-0 list-none">
                  {f.items.map((p) => {
                    const checked = !off.has(keyOf(p));
                    return (
                      <li key={keyOf(p)} className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-xs bg-white rounded-lg" style={{ opacity: checked ? 1 : 0.55 }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleOff(p)} aria-label={`Sélectionner ${p.libelle}`} />
                        <TypePill type={p.type} />
                        <b style={{ color: C.ink }}>{p.libelle}</b>
                        {p.date_fin && <span style={{ color: C.mut }}>fin {dFR(p.date_fin)}</span>}
                        <span style={{ color: C.mut }}>· {NF.format(p.effectif || 0)} pers. · {NF.format(p.nb_bulletins || 0)} bulletin{(p.nb_bulletins || 0) > 1 ? 's' : ''}</span>
                        <b className="ml-auto" style={{ color: C.ink }}>{full(p.net || 0)}</b>
                        {p.approuvee ? (
                          <Pill bg="#E3F7F4" fg="#0E7C70"><Check className="inline w-3 h-3" /> Approuvée</Pill>
                        ) : (
                          <Pill bg="#FFF4D6" fg="#8A6200">En attente d'approbation</Pill>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Net par BU — normale + intérim fusionnés, triable, filtrable, exportable */}
      {rows.length > 0 && (
        <div className="mb-5">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h4 className="m-0 mr-auto text-sm font-bold">Net par BU{tab === 'normal' ? ' · paie normale' : tab === 'interim' ? ' · paie intérim' : ' · normale + intérim'}</h4>
            <label className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg" style={{ background: C.bg }}>
              <Search className="w-3.5 h-3.5" style={{ color: C.mut }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer les BU" aria-label="Filtrer les BU"
                className="w-28 bg-transparent outline-none" />
            </label>
            <ExportBtn onClick={exportRows} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 520 }}>
              <thead><tr className="text-xs text-left" style={{ color: C.mut }}>
                <Th k="bu" right={false}>BU</Th><Th k="eff">Effectif</Th>
                {showN && <Th k="netN">Net normal</Th>}{showI && <Th k="netI">Net intérim</Th>}
                {tab === 'tout' && <Th k="net">Total</Th>}<Th k="delta">Δ période préc.</Th></tr></thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.bu} className="border-t pl-row" style={{ borderColor: C.line }}>
                    <td className="py-2 font-bold">{r.bu}</td>
                    <td className="py-2 text-right">{NF.format(r.eff)}</td>
                    {showN && <td className="py-2 text-right">{NF.format(r.netN)}</td>}
                    {showI && <td className="py-2 text-right">{NF.format(r.netI)}</td>}
                    {tab === 'tout' && (
                      <td className="py-2 pl-3 text-right" style={{ minWidth: 150 }}>
                        <div className="font-bold">{NF.format(r.net)}</div>
                        <div className="flex h-1.5 mt-1 overflow-hidden rounded-full ml-auto" style={{ background: C.line, width: `${Math.max((r.net / maxNet) * 100, 4)}%` }} title={`Normale ${NF.format(r.netN)} · Intérim ${NF.format(r.netI)}`}>
                          <i style={{ width: `${r.net ? (r.netN / r.net) * 100 : 0}%`, background: C.sky }} /><i style={{ width: `${r.net ? (r.netI / r.net) * 100 : 0}%`, background: C.coral }} />
                        </div>
                      </td>
                    )}
                    <td className="py-2 font-bold text-right" style={{ color: r.delta == null ? C.mut : Math.abs(r.delta) > 5 ? '#B4531A' : r.delta >= 0 ? '#0E7C70' : C.mut }}>
                      {r.delta == null ? '—' : signed(r.delta, '%')}</td>
                  </tr>
                ))}
                <tr className="border-t-2" style={{ borderColor: C.navy }}>
                  <td className="py-2 font-bold">Total</td><td className="py-2 font-bold text-right">{NF.format(tot.eff)}</td>
                  {showN && <td className="py-2 font-bold text-right">{NF.format(tot.netN)}</td>}{showI && <td className="py-2 font-bold text-right">{NF.format(tot.netI)}</td>}
                  {tab === 'tout' && <td className="py-2 font-bold text-right">{NF.format(tot.net)}</td>}
                  <td className="py-2 font-bold text-right">{tot.cmpPrec > 0 ? signed(pctChange(tot.cmpNet, tot.cmpPrec), '%') : '—'}</td></tr>
              </tbody>
            </table>
            {shown.length === 0 && <Empty>Aucune BU ne correspond à « {q} ».</Empty>}
          </div>
          {tab === 'tout' && <div className="flex gap-4 mt-2 text-xs" style={{ color: C.mut }}>
            <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full" style={{ background: C.sky }} />Normale</span>
            <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full" style={{ background: C.coral }} />Intérim</span>
            <span>Δ : uniquement sur les périodes qui ont une période précédente archivée</span>
          </div>}
        </div>
      )}

      {/* Zone d'actions : DEUX boutons bien distincts */}
      <div className="flex flex-wrap items-center gap-3 mt-4">
        {can.approuver ? (
          <Btn tone="teal" icon={ShieldCheck} busy={busy} disabled={aApprouver.length === 0} onClick={() => setAsk('approuver')}
            title={aApprouver.length === 0 ? 'Aucune période sélectionnée en attente d\'approbation' : ''}>
            Approbation de la paie{aApprouver.length > 0 ? ` (${aApprouver.length})` : ''}
          </Btn>
        ) : <Locked who="Direction générale" />}
        {can.cloturer ? (
          <Btn tone="navy" icon={Lock} busy={busy} disabled={aCloturer.length === 0} onClick={() => setAsk('cloturer')}
            title={aCloturer.length === 0 ? 'Aucune période approuvée sélectionnée' : ''}>
            Clôture définitive{aCloturer.length > 0 ? ` (${aCloturer.length})` : ''}
          </Btn>
        ) : <Locked who="Président" />}
        {crit > 0 && <span className="inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: '#9A2B1E' }}><AlertTriangle className="w-4 h-4" />{crit} anomalie{crit > 1 ? 's' : ''} critique{crit > 1 ? 's' : ''} à examiner</span>}
      </div>

      {/* Modal Approbation */}
      {ask === 'approuver' && (
        <Modal title="Approbation de la paie" onClose={close}
          footer={<>
            <Btn tone="ghost" onClick={close}>Annuler</Btn>
            <Btn tone="teal" icon={ShieldCheck} disabled={!sure || (crit > 0 && !sureAnom)}
              onClick={() => { const list = aApprouver.map((p) => ({ id: p.id, type: p.type })); close(); onApprouver(list); }}>
              Approuver
            </Btn>
          </>}>
          <p className="m-0 text-sm" style={{ color: C.mut }}>
            Mode : <b style={{ color: C.ink }}>{isConsolidated ? 'Consolidé (toutes filiales)' : `Filiale — ${filialeLabel}`}</b>
          </p>
          <ul className="grid gap-1.5 p-0 mt-3 mb-0 list-none">
            {aApprouver.map((p) => (
              <li key={keyOf(p)} className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg" style={{ background: C.bg }}>
                <TypePill type={p.type} /><b className="truncate">{p.libelle}</b>
                {multiFiliale && <span style={{ color: C.mut }}>· {p.filiale_libelle}</span>}
                <b className="ml-auto whitespace-nowrap">{full(p.net || 0)}</b>
              </li>
            ))}
            <li className="flex items-center justify-between px-3 pt-2 text-sm font-bold"><span>Net total approuvé</span><span>{full(netApprouver)}</span></li>
          </ul>
          <ul className="grid gap-2 pl-5 mt-3 mb-0 text-sm list-disc" style={{ color: C.mut }}>
            <li>Un <b style={{ color: C.ink }}>code d'approbation</b> est généré {multiFiliale ? 'par filiale' : 'pour la période'} (partagé par la paie normale et l'intérim d'une même filiale).</li>
            <li>Les périodes restent <b style={{ color: C.ink }}>ouvertes</b> — une clôture définitive sera requise ensuite.</li>
          </ul>
          {crit > 0 && (
            <div className="flex items-start gap-2 p-3 mt-3 text-xs rounded-xl" style={{ background: '#FFE9E6', color: '#9A2B1E' }}>
              <AlertTriangle className="flex-none w-4 h-4 mt-0.5" />
              <span><b>{crit} anomalie{crit > 1 ? 's' : ''} critique{crit > 1 ? 's' : ''}</b> détectée{crit > 1 ? 's' : ''} par les contrôles ({(controles.checks || []).filter((c) => c.severity === 'critique' && c.count > 0).map((c) => c.label.toLowerCase()).join(', ')}).</span>
            </div>
          )}
          <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer">
            <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} className="mt-1" />
            J'ai contrôlé les salaires nets par BU et j'approuve leur paiement.
          </label>
          {crit > 0 && (
            <label className="flex items-start gap-2 mt-2 text-sm cursor-pointer">
              <input type="checkbox" checked={sureAnom} onChange={(e) => setSureAnom(e.target.checked)} className="mt-1" />
              J'ai pris connaissance des anomalies critiques et j'assume l'approbation.
            </label>
          )}
        </Modal>
      )}

      {/* Modal Clôture définitive */}
      {ask === 'cloturer' && (
        <Modal title="Clôture définitive de la paie" onClose={close}
          footer={<>
            <Btn tone="ghost" onClick={close}>Annuler</Btn>
            <Btn tone="navy" icon={Lock} disabled={!sure}
              onClick={() => { const list = aCloturer.map((p) => ({ id: p.id, type: p.type })); close(); onCloturer(list); }}>
              Clôturer définitivement
            </Btn>
          </>}>
          <p className="m-0 text-sm" style={{ color: C.mut }}>
            Mode : <b style={{ color: C.ink }}>{isConsolidated ? 'Consolidé (toutes filiales)' : `Filiale — ${filialeLabel}`}</b>
          </p>
          <ul className="grid gap-1.5 p-0 mt-3 mb-0 list-none">
            {aCloturer.map((p) => (
              <li key={keyOf(p)} className="px-3 py-2 text-xs rounded-lg" style={{ background: C.bg }}>
                <div className="flex items-center gap-2"><TypePill type={p.type} /><b className="truncate">{p.libelle}</b><b className="ml-auto whitespace-nowrap">{full(p.net || 0)}</b></div>
                <div className="mt-1" style={{ color: C.mut }}>Archive : <code>{archiveName(p)}</code></div>
              </li>
            ))}
            <li className="flex items-center justify-between px-3 pt-2 text-sm font-bold"><span>Net total clôturé</span><span>{full(netCloturer)}</span></li>
          </ul>
          <ul className="grid gap-2 pl-5 mt-3 mb-0 text-sm list-disc" style={{ color: C.mut }}>
            <li>Les périodes approuvées passent à l'état <b style={{ color: C.ink }}>CLÔTURÉE (etat = 2)</b>.</li>
            <li>Les bulletins sont <b style={{ color: C.ink }}>archivés puis retirés</b> de <code>paie_element</code> / <code>paie_element_interim</code> (copie contrôlée ligne à ligne et somme des nets).</li>
            <li>Cette action est <b style={{ color: C.ink }}>irréversible</b>.</li>
          </ul>
          <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer">
            <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} className="mt-1" />
            Je confirme la clôture définitive de {aCloturer.length} période{aCloturer.length > 1 ? 's' : ''}.
          </label>
        </Modal>
      )}
    </>
  );
};

/* ───────── Paie intérim ───────── */
const Interim = ({ data }) => {
  const { money, full } = useFmt();
  const [tab, setTab] = useState('bu');       // 'bu' | 'poste' | 'histo'
  const [metric, setMetric] = useState('net'); // 'net' | 'cout' | 'effectif'
  if (data.actif === false) return <Empty>Aucune filiale de ce périmètre ne traite l'intérim.</Empty>;

  const t = data.interim || {}, p = data.precedent, n = data.normal || {};
  const periodes = data.periodes || [];
  const dNet = p?.net > 0 ? pctChange(t.net, p.net) : null;
  const dCout = p?.cout > 0 ? pctChange(t.cout, p.cout) : null;
  const abs = t.heures_prog > 0 ? (t.heures_abs / t.heures_prog) * 100 : 0;
  const coutHeure = t.heures_payees > 0 ? t.cout / t.heures_payees : 0;
  const fmtM = metric === 'effectif' ? (v) => NF.format(v) : money;
  const rowsBU = (t.par_bu || []).map((r) => ({ ...r, label: r.bu })).sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  const rowsPoste = (t.par_poste || []).map((r) => ({ ...r, label: r.poste })).sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  const histo = (data.historique || []).map((h) => ({ label: moisLabel(h.ym), a: h.normal, b: h.interim, current: h.en_cours }));
  const metrics = [['net', 'Net'], ['cout', 'Coût employeur'], ['effectif', 'Effectif']];

  const exportCSV = () => {
    const src = tab === 'poste' ? rowsPoste : rowsBU;
    downloadCSV(`interim-${tab === 'poste' ? 'par-poste' : 'par-bu'}.csv`, [
      [tab === 'poste' ? 'Poste' : 'BU', 'Effectif', 'Net', 'Brut', 'Charges patronales', 'Coût employeur', 'Heures payées'],
      ...src.map((r) => [r.label, r.effectif, Math.round(r.net), Math.round(r.brut), Math.round(r.charges), Math.round(r.cout), Math.round(r.heures_payees)]),
    ]);
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {periodes.length === 0
          ? <Pill bg="#FFF4D6" fg="#8A6200">Aucune période intérim ouverte</Pill>
          : periodes.map((x) => <Pill key={x.id} bg="#FFE9E6" fg="#9A2B1E">{x.libelle} · {dFR(x.date_debut, { day: 'numeric', month: 'short' })} → {dFR(x.date_fin, { day: 'numeric', month: 'short' })}</Pill>)}
        {p && <span className="text-xs" style={{ color: C.mut }}>Δ vs « {p.periodes?.[0]?.libelle || 'période précédente'} »</span>}
      </div>

      <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <Stat label="Net à payer" value={money(t.net)} delta={dNet} sub={p ? '' : 'pas de période précédente'} />
        <Stat label="Coût employeur" value={money(t.cout)} delta={dCout} sub={`brut ${money(t.brut)}`} />
        <Stat label="Intérimaires" value={NF.format(t.effectif || 0)} sub={`${NF.format(t.bulletins || 0)} bulletin${t.bulletins > 1 ? 's' : ''}`} />
        <Stat label="Heures payées" value={NF.format(Math.round(t.heures_payees || 0))} sub={`${PF.format(abs)} % d'absence · ${coutHeure ? `${full(coutHeure)}/h` : '—'}`} />
      </div>

      <div className="mb-5">
        <div className="mb-1.5 text-xs font-bold" style={{ color: C.mut }}>Où va le coût employeur</div>
        <SplitBar fmt={money} parts={[
          { label: 'Net versé', value: t.net || 0, color: C.teal },
          { label: 'Retenues salariales', value: t.retenues || 0, color: C.sky },
          { label: 'Charges patronales', value: t.charges || 0, color: C.y }]} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Tabs value={tab} onChange={setTab} label="Vue intérim" items={[['bu', 'Par BU'], ['poste', 'Par poste'], ['histo', '12 mois']]} />
        {tab !== 'histo' && (
          <>
            <Tabs value={metric} onChange={setMetric} label="Mesure" items={metrics} />
            <span className="ml-auto"><ExportBtn onClick={exportCSV} /></span>
          </>
        )}
      </div>
      {tab === 'bu' && <HBars rows={rowsBU} valueKey={metric} prevKey={metric === 'net' ? 'net_prec' : undefined} fmt={fmtM} />}
      {tab === 'poste' && <HBars rows={rowsPoste} valueKey={metric} fmt={fmtM} color={C.vio} />}
      {tab === 'histo' && (
        <>
          <div className="flex gap-4 mb-2 text-xs" style={{ color: C.mut }}>
            <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full" style={{ background: C.sky }} />Paie normale</span>
            <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full" style={{ background: C.coral }} />Paie intérim</span>
            <span>Net des périodes clôturées · mois en cours en direct</span>
          </div>
          <StackedBars items={histo} nameA="Normale" nameB="Intérim" colorA={C.sky} colorB={C.coral} />
        </>
      )}
      {n.effectif > 0 && tab !== 'histo' && (
        <p className="mt-4 mb-0 text-xs" style={{ color: C.mut }}>
          L'intérim représente <b style={{ color: C.ink }}>{PF.format((t.cout / ((t.cout + n.cout) || 1)) * 100)} %</b> du coût employeur des filiales concernées.
        </p>
      )}
    </>
  );
};

/* ───────── Coût de la paie : normale vs intérim ───────── */
const Cout = ({ data }) => {
  const { money } = useFmt();
  const [mode, setMode] = useState('montant'); // 'montant' (échelle commune) | 'part' (chaque barre = 100 %)
  if (data.actif === false) return <Empty>Aucune filiale de ce périmètre ne traite l'intérim.</Empty>;

  const cols = [
    { key: 'normal', label: 'Paie normale', d: data.normal || {}, color: C.sky },
    { key: 'interim', label: 'Paie intérim', d: data.interim || {}, color: C.coral },
  ];
  const totalCout = cols.reduce((a, c) => a + (c.d.cout || 0), 0);
  const part = totalCout > 0 ? ((data.interim?.cout || 0) / totalCout) * 100 : 0;
  const maxCout = Math.max(...cols.map((c) => c.d.cout || 0), 1);
  const per = (d) => (d.effectif > 0 ? d.cout / d.effectif : null);
  const perH = (d) => (d.heures_payees > 0 ? d.cout / d.heures_payees : null);
  const chg = (d) => (d.brut > 0 ? (d.charges / d.brut) * 100 : null);
  const abs = (d) => (d.heures_prog > 0 ? (d.heures_abs / d.heures_prog) * 100 : null);
  const metrics = [
    ['Effectif', (d) => (d.effectif ? NF.format(d.effectif) : null)],
    ['Coût employeur', (d) => (d.cout ? money(d.cout) : null)],
    ['Coût moyen / collaborateur', (d) => (per(d) != null ? money(per(d)) : null)],
    ['Coût / heure payée', (d) => (perH(d) != null ? NF.format(Math.round(perH(d))) : null)],
    ['Charges patronales / brut', (d) => (chg(d) != null ? `${PF.format(chg(d))} %` : null)],
    ['Absentéisme (heures)', (d) => (abs(d) != null ? `${PF.format(abs(d))} %` : null)],
  ];

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div>
          <div className="text-xs" style={{ color: C.mut }}>Part de l'intérim dans le coût employeur</div>
          <div className="text-3xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>{PF.format(part)} %</div>
        </div>
        <div className="ml-auto"><Tabs value={mode} onChange={setMode} label="Échelle" items={[['montant', 'Montants'], ['part', 'Parts']]} /></div>
      </div>
      <div className="mb-5"><SplitBar fmt={money} height={10} parts={cols.map((c) => ({ label: c.label, value: c.d.cout || 0, color: c.color }))} /></div>

      <div className="grid gap-4 mb-5">
        {cols.map((c) => (
          <div key={c.key}>
            <div className="mb-1.5 text-xs font-bold" style={{ color: C.ink }}>{c.label}</div>
            <SplitBar fmt={money} denom={mode === 'montant' ? maxCout : undefined} parts={[
              { label: 'Net', value: c.d.net || 0, color: C.teal },
              { label: 'Retenues', value: c.d.retenues || 0, color: C.sky },
              { label: 'Charges patr.', value: c.d.charges || 0, color: C.y }]} />
          </div>
        ))}
      </div>

      <table className="w-full text-sm">
        <thead><tr className="text-xs text-left" style={{ color: C.mut }}><th className="pb-2 font-medium" /><th className="pb-2 font-medium text-right">Normale</th><th className="pb-2 font-medium text-right">Intérim</th></tr></thead>
        <tbody>
          {metrics.map(([label, fn]) => (
            <tr key={label} className="border-t pl-row" style={{ borderColor: C.line }}>
              <td className="py-2" style={{ color: C.mut }}>{label}</td>
              <td className="py-2 font-bold text-right">{fn(data.normal || {}) ?? '—'}</td>
              <td className="py-2 font-bold text-right">{fn(data.interim || {}) ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 mb-0 text-xs" style={{ color: C.mut }}>Coût employeur = brut + charges patronales (CNSS/CNPS, VPS, FDFP, taxes patronales…). Périodes ouvertes des filiales qui traitent l'intérim.</p>
    </>
  );
};

/* ───────── Contrôles avant approbation ───────── */
const SEV = {
  critique: { label: 'Critique', bg: '#FFE9E6', fg: '#9A2B1E' },
  alerte: { label: 'Alerte', bg: '#FFF4D6', fg: '#8A6200' },
  info: { label: 'Info', bg: '#EEEBFE', fg: '#5B4BD0' },
};
const Controles = ({ data }) => {
  const [open, setOpen] = useState(null);
  const [sev, setSev] = useState('tout');
  const checks = data.checks || [];
  const bad = checks.filter((c) => c.count > 0 && (sev === 'tout' || c.severity === sev));
  const okCount = checks.filter((c) => c.count === 0).length;
  const total = data.critiques + data.alertes + data.infos;
  const exportCSV = () => downloadCSV('controles-paie.csv', [
    ['Gravité', 'Contrôle', 'Type de paie', 'Période', 'Collaborateur', 'Détail'],
    ...checks.flatMap((c) => c.items.map((i) => [SEV[c.severity].label, c.label, i.type === 'interim' ? 'Intérim' : 'Normale', i.periode, i.nom || '', i.detail])),
  ]);

  if (data.periodes === 0) return <Empty>Aucune période ouverte à contrôler.</Empty>;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[['tout', 'Tout', total, C.navy, '#fff'], ['critique', 'Critiques', data.critiques, SEV.critique.bg, SEV.critique.fg], ['alerte', 'Alertes', data.alertes, SEV.alerte.bg, SEV.alerte.fg], ['info', 'Infos', data.infos, SEV.info.bg, SEV.info.fg]].map(([k, label, n, bg, fg]) => (
          <button key={k} type="button" aria-pressed={sev === k} onClick={() => setSev(k)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full pl-focus"
            style={{ background: sev === k ? bg : C.bg, color: sev === k ? fg : C.mut, outline: sev === k && k !== 'tout' ? `2px solid ${fg}` : 'none', outlineOffset: -2 }}>
            {label}<span style={{ opacity: 0.8 }}>{n}</span>
          </button>
        ))}
        <span className="ml-auto"><ExportBtn onClick={exportCSV} /></span>
      </div>

      {total === 0 ? (
        <div className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: '#E3F7F4', color: '#0E7C70' }}>
          <Check className="flex-none w-6 h-6" /><div className="text-sm"><b>Aucune anomalie</b><div className="text-xs">{checks.length} contrôles passés sur {data.periodes} période{data.periodes > 1 ? 's' : ''} ouverte{data.periodes > 1 ? 's' : ''}.</div></div>
        </div>
      ) : (
        <ul className="grid gap-2 p-0 m-0 list-none">
          {bad.map((c) => {
            const s = SEV[c.severity], isOpen = open === c.code;
            return (
              <li key={c.code} className="overflow-hidden rounded-xl" style={{ border: `1px solid ${isOpen ? s.fg : C.line}` }}>
                <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : c.code)} className="flex items-center w-full gap-3 px-3 py-2.5 text-left pl-row pl-focus">
                  <span className="flex items-center justify-center flex-none w-8 h-8 rounded-lg" style={{ background: s.bg, color: s.fg }}><AlertTriangle className="w-4 h-4" /></span>
                  <span className="min-w-0"><span className="block text-sm font-bold truncate">{c.label}</span><span className="block text-xs" style={{ color: C.mut }}>{s.label}</span></span>
                  <span className="ml-auto"><Pill bg={s.bg} fg={s.fg}>{NF.format(c.count)}</Pill></span>
                  <ChevronDown className="flex-none w-4 h-4" style={{ color: C.mut, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 overflow-x-auto" style={{ background: C.bg }}>
                    <table className="w-full text-xs">
                      <tbody>
                        {c.items.map((i, idx) => (
                          <tr key={idx} className="border-t" style={{ borderColor: C.line }}>
                            <td className="py-1.5 pr-2"><TypePill type={i.type} /></td>
                            <td className="py-1.5 pr-2 font-bold">{i.nom || '—'}</td>
                            <td className="py-1.5 pr-2" style={{ color: C.mut }}>{i.periode}</td>
                            <td className="py-1.5 text-right">{i.detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {c.count > c.items.length && <p className="mt-2 mb-0 text-xs" style={{ color: C.mut }}>{c.items.length} premiers résultats sur {NF.format(c.count)}.</p>}
                  </div>
                )}
              </li>
            );
          })}
          {bad.length === 0 && <Empty>Aucune anomalie de ce niveau.</Empty>}
        </ul>
      )}
      {okCount > 0 && total > 0 && <p className="mt-3 mb-0 text-xs" style={{ color: C.mut }}><Check className="inline w-3.5 h-3.5 mr-1" style={{ color: C.teal }} />{okCount} contrôle{okCount > 1 ? 's' : ''} sans anomalie.</p>}
    </>
  );
};

const Logs = ({ data }) => (
  data.length === 0 ? <Empty>Aucune activité récente.</Empty> : (
    <ul className="grid gap-3 p-0 m-0 list-none">
      {data.slice(0, 10).map((l, i) => (
        <li key={l.id ?? i} className="flex items-start gap-3">
          <span className="flex items-center justify-center flex-none w-8 h-8 text-xs font-bold rounded-full" style={{ background: PALETTE[(l.username?.length || i) % 5], color: C.navy }}>{initials(l.username || l.user_name)}</span>
          <div className="min-w-0 text-sm leading-snug">
            <b>{l.username || l.user_name}</b> <span style={{ color: C.mut }}>{l.action}</span>
            {l.description && <span className="block text-xs mt-0.5" style={{ color: C.ink }}>{l.description}</span>}
            <div className="text-xs" style={{ color: C.mut }}>{ago(l.created_at)}</div>
          </div>
        </li>
      ))}
    </ul>
  )
);

const TurnoverDepartement = ({ data }) => {
  const total = data?.reduce((a, d) => a + (d.nombre_sorties || 0), 0) || 0;

  if (!data || data.length === 0) {
    return <Empty>Aucune donnée de départ par département disponible</Empty>;
  }

  const chartData = data.map((d, i) => ({
    label: d.departement || 'Non défini',
    value: d.nombre_sorties || 0,
  }));

  return (
    <>
      <div className="flex items-end gap-3 mb-4">
        <div>
          <div className="text-xs" style={{ color: C.mut }}>Total des départs</div>
          <div className="text-2xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>
            {NF.format(total)}
          </div>
        </div>
        <Pill bg="#EEEBFE" fg="#5B4BD0">{data.length} département{data.length > 1 ? 's' : ''}</Pill>
      </div>

      <Bars data={chartData} height={220} />
    </>
  );
};

const TurnoverPatterns = ({ data }) => {
  const { patterns, alerteCourante, alerteProchaine, moyenneGlobale, seuilAlerte, seuilCritique } = data || {};

  if (!patterns || patterns.length === 0) {
    return <Empty>Données insuffisantes pour l'analyse des patterns (3 ans requis)</Empty>;
  }

  const maintenant = new Date();
  const moisActuel = maintenant.getMonth() + 1;

  // Données pour le graphique
  const chartData = patterns.map(p => ({
    label: p.libelle,
    value: p.moyenne,
    niveau: p.niveau,
    isCurrent: p.mois === moisActuel
  }));

  const maxVal = Math.max(...chartData.map(d => d.value), 1);

  return (
    <>
      {/* Alerte en cours */}
      {alerteCourante && (
        <div className={`flex items-start gap-2 p-3 mb-4 rounded-xl ${alerteCourante.niveau === 'critique' ? 'bg-[#FFE9E6]' : 'bg-[#FFF4D6]'}`}
             style={{ color: alerteCourante.niveau === 'critique' ? '#9A2B1E' : '#8A6200' }}>
          <AlertTriangle className="flex-none w-5 h-5 mt-0.5" />
          <div className="text-sm">
            <b>Période à forte turnover en cours</b>
            <div className="text-xs">
              {alerteCourante.libelle} : {NF.format(alerteCourante.moyenne)} départs en moyenne
              (vs {NF.format(moyenneGlobale)} normalement)
            </div>
          </div>
        </div>
      )}

      {/* Alerte prochaine */}
      {alerteProchaine && !alerteCourante && (
        <div className={`flex items-start gap-2 p-3 mb-4 rounded-xl ${alerteProchaine.niveau === 'critique' ? 'bg-[#FFE9E6]' : 'bg-[#FFF4D6]'}`}
             style={{ color: alerteProchaine.niveau === 'critique' ? '#9A2B1E' : '#8A6200' }}>
          <AlertTriangle className="flex-none w-5 h-5 mt-0.5" />
          <div className="text-sm">
            <b>Alerte : période à risque le mois prochain</b>
            <div className="text-xs">
              {alerteProchaine.libelle} : {NF.format(alerteProchaine.moyenne)} départs en moyenne
              (vs {NF.format(moyenneGlobale)} normalement)
            </div>
          </div>
        </div>
      )}

      {/* Pas d'alerte */}
      {!alerteCourante && !alerteProchaine && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-xl" style={{ background: '#E3F7F4', color: '#0E7C70' }}>
          <Check className="flex-none w-5 h-5" />
          <div className="text-sm">
            <b>Période stable</b>
            <div className="text-xs">Aucune période à forte turnover détectée à l'approche</div>
          </div>
        </div>
      )}

      {/* Graphique des patterns */}
      <div className="mb-4">
        <div className="mb-2 text-xs font-bold" style={{ color: C.mut }}>
          Moyenne mensuelle sur 3 ans (seuil alerte: {NF.format(seuilAlerte)}, critique: {NF.format(seuilCritique)})
        </div>
        <div className="flex items-end gap-1 h-40">
          {chartData.map((d, i) => {
            const height = (d.value / maxVal) * 100;
            const bg = d.niveau === 'critique' ? C.coral : d.niveau === 'alerte' ? C.y : C.sky;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full rounded-t transition-all hover:opacity-80"
                  style={{
                    height: `${Math.max(height, 4)}%`,
                    background: bg,
                    opacity: d.isCurrent ? 1 : 0.7,
                    border: d.isCurrent ? `2px solid ${C.navy}` : 'none'
                  }}
                  title={`${d.label} : ${NF.format(d.value)} départs`}
                />
                <span className="text-xs" style={{ color: d.isCurrent ? C.ink : C.mut, fontWeight: d.isCurrent ? 700 : 400 }}>
                  {d.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Légende */}
      <div className="flex items-center justify-center gap-4 text-xs" style={{ color: C.mut }}>
        <span className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ background: C.coral }} />
          Critique
        </span>
        <span className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ background: C.y }} />
          Alerte
        </span>
        <span className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ background: C.sky }} />
          Normal
        </span>
      </div>
    </>
  );
};

// Compte rendu d'une approbation / clôture : codes à copier, tables d'archive créées
const ResultModal = ({ result, onClose }) => {
  const { full } = useFmt();
  const [copied, setCopied] = useState(null);
  const copy = async (txt, k) => { try { await navigator.clipboard.writeText(txt); setCopied(k); setTimeout(() => setCopied(null), 1800); } catch { /* silencieux */ } };
  const Code = ({ code, k }) => (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-bold rounded-lg" style={{ background: C.bg, fontFamily: 'ui-monospace, monospace' }}>
      {code}
      <button type="button" onClick={() => copy(code, k)} aria-label={`Copier ${code}`} className="rounded pl-focus">{copied === k ? <Check className="w-4 h-4" style={{ color: C.teal }} /> : <Copy className="w-4 h-4" />}</button>
    </span>
  );
  const approuve = result.kind === 'approuver';
  return (
    <Modal title={approuve ? 'Paie approuvée' : 'Paie clôturée et archivée'} onClose={onClose} footer={<Btn tone="navy" onClick={onClose}>Fermer</Btn>}>
      <div className="flex items-center gap-3 p-3 mb-4 rounded-xl" style={{ background: '#E3F7F4', color: '#0E7C70' }}>
        <Check className="flex-none w-5 h-5" /><span className="text-sm font-bold">{result.message}</span>
      </div>
      {approuve ? (
        <ul className="grid gap-3 p-0 m-0 list-none">
          {(result.par_filiale || []).map((f) => (
            <li key={f.filiale_id} className="p-3 rounded-xl" style={{ border: `1px solid ${C.line}` }}>
              <div className="flex flex-wrap items-center gap-2 mb-2"><b className="text-sm">{f.filiale_libelle}</b></div>
              <div className="flex flex-wrap gap-1.5">{f.periodes.map((p) => <span key={`${p.type}${p.id}`} className="inline-flex items-center gap-1.5 text-xs"><TypePill type={p.type} />{p.libelle}</span>)}</div>
            </li>
          ))}
          {result.lot && <li className="text-xs" style={{ color: C.mut }}>Lot d'approbation : <b>{result.lot}</b></li>}
        </ul>
      ) : (
        <>
          <ul className="grid gap-2 p-0 m-0 list-none">
            {(result.archives || []).map((a) => (
              <li key={`${a.type}${a.idpaie}`} className="px-3 py-2 text-xs rounded-lg" style={{ background: C.bg }}>
                <div className="flex items-center gap-2"><TypePill type={a.type} /><b className="truncate">{a.libelle}</b><b className="ml-auto whitespace-nowrap">{full(Number(a.montant_net))}</b></div>
                <div className="mt-1" style={{ color: C.mut }}>{a.table ? <><code>{a.table}</code> · {NF.format(a.nb_elements)} bulletin{a.nb_elements > 1 ? 's' : ''} archivé{a.nb_elements > 1 ? 's' : ''}</> : 'Aucun bulletin à archiver'}</div>
                {(a.perf || []).map((x) => (
                  <div key={x.table} className="mt-0.5" style={{ color: C.mut }}>
                    <code>{x.table}</code> · {NF.format(x.nb)} ligne{x.nb > 1 ? 's' : ''}
                  </div>
                ))}
              </li>
            ))}
          </ul>
          {(result.cloture || []).length > 0 && (
            <div className="grid gap-2 mt-4">
              <div className="text-xs font-bold" style={{ color: C.mut }}>Codes de clôture</div>
              {result.cloture.map((c) => (
                <div key={c.filiale_id} className="flex flex-wrap items-center gap-2 text-sm"><b>{c.filiale_libelle || `Filiale ${c.filiale_id}`}</b><span className="ml-auto"><Code code={c.code} k={`c-${c.filiale_id}`} /></span></div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

/* ═════════ Coque d'un widget : déplacer · redimensionner · réduire · agrandir ═════════ */
const FRAC = { 4: '⅓', 6: '½', 8: '⅔', 12: '1/1' };

const Shell = ({ id, w, idx, edit, isXl, span, resizing, collapsed, dnd, summary, onMove, onLive, onSpan, onHide, onCollapse, onFocus, children }) => {
  const Icon = w.icon;
  const sizes = sizesOf(id);
  const [armed, setArmed] = useState(false); // le widget n'est « attrapable » que par sa poignée
  const closed = collapsed && !edit;
  const ib = 'flex items-center justify-center w-7 h-7 rounded-lg pl-focus';
  const bs = { background: '#fff', color: C.navy, border: `1px solid ${C.line}` };

  // Redimensionnement : on tire le coin, la largeur « accroche » aux tailles autorisées
  const startResize = (e) => {
    e.preventDefault();
    const el = e.currentTarget.closest('section'), grid = el.parentElement, gap = 20;
    const unit = (grid.clientWidth + gap) / 12;
    const pick = (x) => {
      const raw = (x - el.getBoundingClientRect().left + gap) / unit;
      return sizes.reduce((b, n) => (Math.abs(n - raw) < Math.abs(b - raw) ? n : b), sizes[0]);
    };
    const move = (ev) => onLive(pick(ev.clientX));
    const up = (ev) => {
      ['pointermove', 'pointerup', 'pointercancel'].forEach((t) => window.removeEventListener(t, t === 'pointermove' ? move : up));
      document.body.style.userSelect = ''; document.body.style.cursor = '';
      onSpan(pick(ev.clientX));
    };
    document.body.style.userSelect = 'none'; document.body.style.cursor = 'ew-resize';
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
  };

  const handlers = edit ? {
    draggable: armed,
    onDragStart: (e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', id); dnd.setDrag(id); },
    onDragEnd: () => { setArmed(false); dnd.setDrag(null); dnd.setOver(null); },
    onDragOver: (e) => { if (dnd.drag) { e.preventDefault(); dnd.setOver(id); } },
    onDrop: (e) => { e.preventDefault(); if (dnd.drag && dnd.drag !== id) dnd.drop(dnd.drag, id); dnd.setDrag(null); dnd.setOver(null); setArmed(false); },
  } : {};

  const dim = edit ? { opacity: 0.55, pointerEvents: 'none' } : undefined;
  return (
    <section {...handlers} aria-label={w.title} className={`pl-w pl-in relative ${w.bare ? '' : 'pl-card'} ${closed ? 'pl-collapsed' : ''}`}
      style={{
        '--span': span, '--span-md': span <= 6 ? 6 : 12, animationDelay: `${idx * 70}ms`, opacity: dnd.drag === id ? 0.4 : 1,
        outline: edit ? `2px dashed ${dnd.over === id ? C.y : resizing ? C.navy2 : '#B9C1E0'}` : undefined, outlineOffset: 4,
        borderRadius: w.bare ? 24 : 18,
      }}>
      {edit && (
        <div className="flex flex-wrap items-center gap-2 p-1.5 mb-3 rounded-xl" style={{ background: w.bare ? '#fff' : C.bg, border: w.bare ? `1px solid ${C.line}` : 'none' }}>
          <div className="flex items-center gap-1.5 pr-2 text-xs font-bold select-none" style={{ cursor: 'grab' }} title="Glisser pour déplacer"
            onMouseDown={() => setArmed(true)} onMouseUp={() => setArmed(false)}>
            <GripVertical className="w-4 h-4" style={{ color: C.mut }} />
            <span className="truncate" style={{ maxWidth: 150 }}>{w.title}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1 ml-auto">
            <button type="button" className={ib} style={bs} onClick={() => onMove(-1)} aria-label="Monter"><ArrowUp className="w-4 h-4" /></button>
            <button type="button" className={ib} style={bs} onClick={() => onMove(1)} aria-label="Descendre"><ArrowDown className="w-4 h-4" /></button>
            {isXl && sizes.length > 1 && (
              <div role="group" aria-label="Largeur du widget" className="flex items-center gap-0.5 p-0.5 rounded-lg" style={bs}>
                {sizes.map((n) => (
                  <button key={n} type="button" onClick={() => onSpan(n)} aria-pressed={span === n} aria-label={`Largeur ${n} colonnes sur 12`}
                    className="h-6 px-2 text-xs font-bold rounded-md pl-focus" style={{ background: span === n ? C.navy : 'transparent', color: span === n ? '#fff' : C.navy }}>{FRAC[n]}</button>
                ))}
              </div>
            )}
            <button type="button" className={ib} style={bs} onClick={onHide} aria-label="Masquer"><EyeOff className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {!w.bare && (
        <h3 className="flex items-center gap-2 text-lg" style={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: '-.02em', marginBottom: closed ? 0 : 16, transition: 'margin .3s' }}>
          <span className="flex items-center justify-center flex-none w-8 h-8 rounded-lg" style={{ background: `${w.color}33`, color: C.navy }}><Icon className="w-4 h-4" /></span>
          <span className="truncate">{w.title}</span>
          <span className="flex items-center gap-2 ml-auto">
            {summary && !edit && <small className="text-xs font-bold whitespace-nowrap" style={{ color: closed ? C.ink : C.mut, fontFamily: BODY, fontWeight: closed ? 700 : 400 }}>{summary}</small>}
            {!edit && (
              <span className="flex items-center gap-0.5 pl-acts">
                <button type="button" onClick={onCollapse} aria-expanded={!collapsed} aria-label={`${collapsed ? 'Déployer' : 'Réduire'} : ${w.title}`} className={ib} style={{ color: C.mut }}>
                  <ChevronDown className="w-4 h-4" style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .25s' }} />
                </button>
                <button type="button" onClick={onFocus} aria-label={`Agrandir : ${w.title}`} className={ib} style={{ color: C.mut }}><Maximize2 className="w-4 h-4" /></button>
              </span>
            )}
          </span>
        </h3>
      )}

      {w.bare
        ? <div style={dim}>{children}</div>
        : <div className="pl-collapse" data-c={closed ? 1 : 0}><div style={dim}>{children}</div></div>}

      {!w.bare && w.description && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: C.line }}>
          <p className="text-xs leading-relaxed" style={{ color: C.mut }}>
            {w.description}
          </p>
        </div>
      )}

      {edit && isXl && sizes.length > 1 && (
        <span role="presentation" onPointerDown={startResize} title="Tirer pour redimensionner" className="absolute flex items-end justify-end w-7 h-7"
          style={{ right: 6, bottom: 6, cursor: 'ew-resize', touchAction: 'none' }}>
          <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M11 1L1 11M11 6L6 11" stroke={C.mut} strokeWidth="1.6" strokeLinecap="round" /></svg>
        </span>
      )}
      {resizing && (
        <span className="absolute px-3 py-1 text-xs font-bold text-white rounded-full pointer-events-none" style={{ left: '50%', bottom: 10, transform: 'translateX(-50%)', background: C.navy }}>
          {span}/12 · {FRAC[span]}
        </span>
      )}
    </section>
  );
};

/* ═════════ Page ═════════ */
const useMedia = (q) => {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q); const f = () => setM(mq.matches);
    mq.addEventListener('change', f); f();
    return () => mq.removeEventListener('change', f);
  }, [q]);
  return m;
};

const API = {
  rh: (p) => http.get('/api/dashboard/rh', { params: { filiale_id: p.filiale_id } }),
  masse: (p) => http.get('/api/dashboard/masse-mensuelle', { params: { filiale_id: p.filiale_id } }),
  contrats: (p) => http.get('/api/contrats-validation/a-valider', { params: { filiale_id: p.filiale_id } }),
  contrats_expiration: (p) => http.get('/api/contrats-validation/expiration', { params: { filiale_id: p.filiale_id } }),
  effectifs: (p) => http.get('/api/contrats-validation/effectifs', { params: { filiale_id: p.filiale_id } }),
  pyramide_ages: (p) => http.get('/api/dashboard/pyramide-ages', { params: { filiale_id: p.filiale_id } }),
  cooptation: (p) => http.get('/api/dashboard/cooptation', { params: { filiale_id: p.filiale_id } }),
  responsabilites: (p) => http.get('/api/dashboard/responsabilites', { params: { filiale_id: p.filiale_id } }),
  masse_activite: (p) => http.get('/api/dashboard/masse-activite', { params: { filiale_id: p.filiale_id } }),
  caisse_sociale: (p) => http.get('/api/dashboard/caisse-sociale', { params: { filiale_id: p.filiale_id } }),
  turnover_evol: (p) => http.get('/api/personnel/stats-sortie-par-date', { params: { date_debut: p.date_debut, date_fin: p.date_fin, filiale_id: p.filiale_id } }),
  turnover_motif: (p) => http.get('/api/personnel/stats-sortie-par-motif', { params: { date_debut: p.date_debut, date_fin: p.date_fin, filiale_id: p.filiale_id } }),
  turnover_departement: (p) => http.get('/api/employes-sortis/stats-par-departement', { params: { date_debut: p.date_debut, date_fin: p.date_fin, filiale_id: p.filiale_id } }),
  turnover_patterns: (p) => http.get('/api/employes-sortis/patterns-turnover', { params: { filiale_id: p.filiale_id } }),
  periodes: (p) => http.get('/api/paie/periodes-ouvertes', { params: { filiale_id: p.filiale_id } }),
  interim: (p) => http.get('/api/paie/interim', { params: { filiale_id: p.filiale_id } }),
  controles: (p) => http.get('/api/paie/controles', { params: { filiale_id: p.filiale_id } }),
  logs: () => http.get('/api/logs/recent', { params: { limit: 10 } }),
};
// Blocs qui bougent pendant le traitement de la paie : rafraîchis automatiquement
const LIVE_KEYS = ['periodes', 'interim', 'controles', 'contrats', 'logs'];
// Après une approbation / clôture, tout ce qui en dépend est rechargé
const AFTER_PAIE = ['periodes', 'interim', 'controles'];

// Nettoie des préférences enregistrées (widgets disparus, tailles invalides, ancien format « wide »)
const clean = (p) => {
  const order = [...(p.order || []).filter((i) => WIDGETS[i]), ...ORDER.filter((i) => !(p.order || []).includes(i))];
  const size = {};
  Object.entries(p.size || {}).forEach(([k, v]) => { if (WIDGETS[k] && sizesOf(k).includes(v)) size[k] = v; });
  (p.wide || []).forEach((k) => { if (WIDGETS[k] && !size[k]) size[k] = 12; });
  return {
    preset: p.preset || 'custom', order, size,
    hidden: (p.hidden || []).filter((i) => WIDGETS[i]),
    collapsed: (p.collapsed || []).filter((i) => WIDGETS[i] && !WIDGETS[i].bare),
  };
};
const fromPreset = (k) => clean({ preset: k, order: PRESETS[k].order, hidden: ORDER.filter((i) => !PRESETS[k].order.includes(i)) });

const PilotageDashboard = () => {
  const { user } = useAuth();
  const { currentFiliale, isConsolidated } = useFiliale();
  const prefKey = `talentia.pilotage.v1.${user?.id ?? 'anon'}`;
  const role = roleOf(user);
  const can = useMemo(() => Object.fromEntries(Object.entries(RULES).map(([k, re]) => [k, re.test(role)])), [role]);
  const fmt = useMemo(() => makeFmt(isConsolidated ? '' : currentFiliale?.devise || ''), [isConsolidated, currentFiliale]);

  const [data, setData] = useState({});
  const [errors, setErrors] = useState({}); // { source: { msg, detail } } — une source en erreur n'affecte que ses widgets
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);     // actualisation manuelle
  const [acting, setActing] = useState(false); // approbation / clôture en cours
  const [updated, setUpdated] = useState(null);
  const [liveOn, setLiveOn] = useState(true);
  const [prefs, setPrefs] = useState(() => fromPreset('rh'));
  const [edit, setEdit] = useState(false);
  const [configModal, setConfigModal] = useState(false);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [live, setLive] = useState(null);   // redimensionnement en cours : { id, span }
  const [focus, setFocus] = useState(null); // widget agrandi en plein écran
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);
  const isXl = useMedia('(min-width: 1280px)');
  const tRef = useRef(null);
  const reqRef = useRef(0);

  const params = useMemo(() => {
    const base = !isConsolidated && currentFiliale?.id ? { filiale_id: currentFiliale.id } : {};
    // Calculer les dates pour les 12 derniers mois pour le turnover
    const now = new Date();
    const dateFin = now.toISOString().split('T')[0];
    const dateDebut = new Date(now.getFullYear() - 1, now.getMonth(), 1).toISOString().split('T')[0];
    return { ...base, date_debut: dateDebut, date_fin: dateFin };
  }, [isConsolidated, currentFiliale]);
  const flash = (msg, tone = 'ok') => { setToast({ msg, tone }); clearTimeout(tRef.current); tRef.current = setTimeout(() => setToast(null), tone === 'err' ? 6500 : 3200); };

  // Préférences : chargées par utilisateur, enregistrées uniquement sur action de l'utilisateur
  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(prefKey)); } catch { /* ignoré */ }
    setPrefs(saved ? clean(saved) : fromPreset(profilDefaut(user)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefKey]);
  const commit = (p) => { const c = clean(p); setPrefs(c); try { localStorage.setItem(prefKey, JSON.stringify(c)); } catch { /* ignoré */ } };
  const toggleWidget = (id) => {
    const hidden = prefs.hidden.includes(id);
    commit({ ...prefs, hidden: hidden ? prefs.hidden.filter((x) => x !== id) : [...prefs.hidden, id] });
  };

  // Chargement : chaque source est indépendante. En cas d'échec on garde les dernières données connues (si on en a) et on signale l'erreur.
  // `reset` = changement de périmètre (filiale) : toute réponse encore en vol pour l'ancien périmètre est ignorée.
  const load = useCallback(async (keys = Object.keys(API), { reset = false } = {}) => {
    const id = reset ? ++reqRef.current : reqRef.current;
    const res = await Promise.allSettled(keys.map((k) => API[k](params)));
    if (id !== reqRef.current) return;
    const ok = (r) => r.status === 'fulfilled' && r.value?.data != null;
    setData((d) => { const n = { ...d }; res.forEach((r, i) => { if (ok(r)) n[keys[i]] = r.value.data; else if (!(keys[i] in n)) n[keys[i]] = null; }); return n; });
    setErrors((e) => {
      const n = { ...e };
      res.forEach((r, i) => { if (ok(r)) delete n[keys[i]]; else n[keys[i]] = { msg: errMsg(r.reason), detail: errDetail(r.reason) }; });
      return n;
    });
    setUpdated(new Date());
    setLoading(false);
  }, [params]);

  useEffect(() => { setLoading(true); setData({}); setErrors({}); load(undefined, { reset: true }); }, [load]);

  // Actualisation automatique (en pause quand l'onglet est caché)
  useEffect(() => {
    if (!liveOn) return undefined;
    const t = setInterval(() => { if (!document.hidden) load(LIVE_KEYS); }, LIVE_EVERY);
    return () => clearInterval(t);
  }, [liveOn, load]);

  const refresh = async () => { setBusy(true); await load(); setBusy(false); flash('Données actualisées'); };
  const retry = (keys) => load(keys);

  // Action métier : appel API réel puis rechargement des sources concernées. Retourne la réponse (ou null en cas d'échec).
  const act = async (call, okMsg, refreshKeys = []) => {
    try { const r = await call(); flash(okMsg); await load(refreshKeys); return r?.data ?? true; }
    catch (e) { flash(errMsg(e), 'err'); return null; }
  };
  const onContrat = (c, verbe) => {
    if (verbe === 'rejeter' && c && !window.confirm(`Rejeter le contrat de ${c.prenom} ${c.nom} ?`)) return;
    if (verbe === 'rejeter-tout' && !window.confirm('Rejeter tous les contrats en attente ?')) return;
    if (verbe === 'valider-tout' && !window.confirm('Valider tous les contrats en attente ?')) return;

    if (verbe === 'valider-tout' || verbe === 'rejeter-tout') {
      // périmètre envoyé dans le corps ET dans l'URL (le serveur lit l'un ou l'autre)
      act(() => http.put(`/api/contrats-validation/${verbe}`, { ...params, params }, { params }), verbe === 'valider-tout' ? 'Tous les contrats validés' : 'Tous les contrats rejetés', ['contrats', 'rh', 'effectifs']);
    } else {
      act(() => http.put(`/api/contrats-validation/${c.idpersonnel_contrat}/${verbe}`), verbe === 'valider' ? 'Contrat validé' : 'Contrat rejeté', ['contrats', 'rh', 'effectifs']);
    }
  };
  const onControl = (f, v) => act(() => http.put(`/api/paie/virements/${f.id}/controle`, { controle: v }), v ? 'Fichier contrôlé' : 'Contrôle retiré', ['virements', 'processus']);
  const onOrder = () => act(() => http.post('/api/paie/virements/ordre'), 'Ordre de virement émis', ['virements', 'processus']);

  // Les périodes sont envoyées sous forme de couples { id, type } : un même id existe dans paie ET paie_interime
  const onApprouver = async (list) => {
    setActing(true);
    const r = await act(() => http.post('/api/paie/periodes/approuver', { periodes: list, ...params }), 'Périodes approuvées', AFTER_PAIE);
    setActing(false);
    if (r && r !== true) setResult({ kind: 'approuver', ...r });
  };
  const onCloturer = async (list) => {
    setActing(true);
    const r = await act(() => http.post('/api/paie/periodes/cloturer', { periodes: list, ...params }), 'Périodes clôturées définitivement', [...AFTER_PAIE, 'masse', 'rh']);
    setActing(false);
    if (r && r !== true) setResult({ kind: 'cloturer', ...r });
  };

  // Mise en page
  const visible = prefs.order.filter((i) => !prefs.hidden.includes(i));
  const spanOf = (id) => (live?.id === id ? live.span : prefs.size[id] ?? WIDGETS[id].span);
  const setSpan = (id, n) => commit({ ...prefs, preset: 'custom', size: { ...prefs.size, [id]: n } });
  const move = (id, dir) => {
    const t = visible[visible.indexOf(id) + dir]; if (!t) return;
    const o = [...prefs.order], a = o.indexOf(id), b = o.indexOf(t); [o[a], o[b]] = [o[b], o[a]];
    commit({ ...prefs, order: o, preset: 'custom' });
  };
  const drop = (id, target) => { const o = [...prefs.order]; o.splice(o.indexOf(id), 1); o.splice(o.indexOf(target), 0, id); commit({ ...prefs, order: o, preset: 'custom' }); };
  const toggle = (k, id) => commit({ ...prefs, preset: 'custom', [k]: prefs[k].includes(id) ? prefs[k].filter((i) => i !== id) : [...prefs[k], id] });

  const body = (id) => {
    const w = WIDGETS[id];
    const missing = w.src.filter((k) => !data[k]);
    if (missing.length) {
      if (loading || missing.every((k) => !(k in errors))) return <Skeleton />;
      return <ErrorBox errors={errors} keys={missing} onRetry={() => retry(missing)} />;
    }
    switch (id) {
      case 'kpis': return <KpiStrip rh={data.rh} masse={data.masse} effectifs={data.effectifs} periodes={data.periodes} interim={data.interim} />;
      case 'masse': return <Masse data={data.masse} />;
      case 'contrats_repart': return <Donut data={data.rh.repartition} ariaLabel="Répartition des contrats" centerLabel="contrats" />;
      case 'effectif_evol': return <LineChart labels={MOIS} series={[{ name: 'Effectif', data: data.rh.effectif_mensuel, color: C.sky, area: true }]} fmt={(v) => NF.format(Math.round(v))} />;
      case 'effectif_bu': return <Bars data={data.rh.par_bu} ariaLabel="Effectif par Business Unit" />;
      case 'pyramide_ages': return <PyramideAges data={data.pyramide_ages} />;
      case 'cooptation': return <Cooptation data={data.cooptation} />;
      case 'responsabilites': return <Responsabilites data={data.responsabilites} />;
      case 'masse_activite': return <MasseActivite data={data.masse_activite} filialeCode={currentFiliale?.code} devise={currentFiliale?.devise} />;
      case 'caisse_sociale': return <CaisseSociale data={data.caisse_sociale} devise={currentFiliale?.devise} />;
      case 'turnover_evol': return <LineChart labels={data.turnover_evol?.map(d => d.periode) || []} series={[{ name: 'Départs', data: data.turnover_evol?.map(d => d.nombre_sorties) || [], color: C.coral, area: true }]} fmt={(v) => NF.format(Math.round(v))} />;
      case 'turnover_motif': return <Donut data={data.turnover_motif?.map(d => ({ label: d.motif || 'Non défini', value: d.nombre })) || []} ariaLabel="Répartition par motif de sortie" centerLabel="motifs" />;
      case 'turnover_departement': return <TurnoverDepartement data={data.turnover_departement} />;
      case 'turnover_patterns': return <TurnoverPatterns data={data.turnover_patterns} />;
      case 'timeline': return <Timeline data={data.processus} />;
      case 'periodes': return <Periodes data={data.periodes} controles={data.controles} can={can} busy={acting} onApprouver={onApprouver} onCloturer={onCloturer} isConsolidated={isConsolidated} filialeLabel={currentFiliale?.libelle} />;
      case 'controles': return <Controles data={data.controles} />;
      case 'interim': return <Interim data={data.interim} />;
      case 'cout': return <Cout data={data.interim} />;
      case 'virements': return <Virements data={data.virements} canControl={can.controlerVirement} canOrder={can.genererOrdre} onControl={onControl} onOrder={onOrder} />;
      case 'contrats': return <Contrats data={data.contrats} can={can.validerContrat} onAct={onContrat} />;
      case 'contrats_expiration': return <ContratsExpiration data={data.contrats_expiration} />;
      case 'logs': return <Logs data={data.logs} />;
      default: return null;
    }
  };

  // Résumé d'une ligne : visible dans l'en-tête, et seul affiché quand le widget est réduit
  const summary = (id) => {
    const d = data;
    switch (id) {
      case 'masse': return d.masse ? `${fmt.money((d.masse.annee || []).reduce((a, v) => a + (v || 0), 0))} cumulés` : null;
      case 'contrats_repart': case 'effectif_evol': case 'effectif_bu': return d.rh ? `${NF.format(d.rh.kpis.effectif)} collaborateurs` : null;
      case 'pyramide_ages': return d.pyramide_ages ? `${NF.format(d.pyramide_ages.total)} collaborateurs` : null;
      case 'cooptation': return d.cooptation ? `${PF.format(d.cooptation.taux)} % cooptation` : null;
      case 'responsabilites': return d.responsabilites ? `${PF.format(d.responsabilites.moyenne)} subordonné${d.responsabilites.moyenne > 1 ? 's' : ''}/manager` : null;
      case 'masse_activite': return d.masse_activite ? `${d.masse_activite.activites.length} activité${d.masse_activite.activites.length > 1 ? 's' : ''}` : null;
      case 'caisse_sociale': return d.caisse_sociale ? fmt.money(d.caisse_sociale.montant_actuel) : null;
      case 'turnover_evol': return d.turnover_evol ? `${d.turnover_evol.reduce((a, v) => a + (v.nombre_sorties || 0), 0)} départs` : null;
      case 'turnover_motif': return d.turnover_motif ? `${d.turnover_motif.length} motif${d.turnover_motif.length > 1 ? 's' : ''}` : null;
      case 'turnover_departement': return d.turnover_departement ? `${d.turnover_departement.length} département${d.turnover_departement.length > 1 ? 's' : ''}` : null;
      case 'turnover_patterns': return d.turnover_patterns ? (d.turnover_patterns.alerteCourante ? '⚠️ À risque' : d.turnover_patterns.alerteProchaine ? '🔴 Prochaine alerte' : '✓ Stable') : null;
      case 'timeline': return d.processus ? (d.processus.etape > ETAPES.length ? 'Terminé' : `Étape ${d.processus.etape}/${ETAPES.length}`) : null;
      case 'periodes': { const n = d.periodes?.periodes?.length || 0; return d.periodes ? (n ? `${n} ouverte${n > 1 ? 's' : ''}` : d.periodes.cloture ? 'Clôturées' : 'Aucune ouverte') : null; }
      case 'controles': return d.controles ? (d.controles.periodes === 0 ? 'Rien à contrôler' : d.controles.critiques ? `${d.controles.critiques} critique${d.controles.critiques > 1 ? 's' : ''}` : d.controles.alertes ? `${d.controles.alertes} alerte${d.controles.alertes > 1 ? 's' : ''}` : 'Aucune anomalie') : null;
      case 'interim': return d.interim?.actif ? `${NF.format(d.interim.interim.effectif)} intérimaire${d.interim.interim.effectif > 1 ? 's' : ''} · ${fmt.money(d.interim.interim.net)}` : d.interim ? 'Non applicable' : null;
      case 'cout': { if (!d.interim?.actif) return d.interim ? 'Non applicable' : null; const a = d.interim.interim.cout, b = d.interim.normal.cout; return `${PF.format(a + b ? (a / (a + b)) * 100 : 0)} % intérim`; }
      case 'virements': { const f = d.virements?.fichiers || []; return d.virements ? (d.virements.ordre ? 'Ordre émis' : `${f.filter((x) => x.statut === 'controle').length}/${f.length} contrôlés`) : null; }
      case 'contrats': return d.contrats ? `${d.contrats.length} en attente` : null;
      case 'contrats_expiration': return d.contrats_expiration ? `${d.contrats_expiration.count} contract${d.contrats_expiration.count > 1 ? 's' : ''}` : null;
      case 'logs': return d.logs ? '10 dernières' : null;
      default: return null;
    }
  };

  const nErr = Object.keys(errors).length;
  const scope = isConsolidated ? 'Vue consolidée du groupe' : currentFiliale?.libelle || 'Votre filiale';
  const dateLabel = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const hhmm = updated?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <FmtCtx.Provider value={fmt}>
      <div className="min-h-screen px-6 py-7 lg:px-9" style={{ background: C.bg, color: C.ink, fontFamily: BODY }}>
        <style>{CSS}</style>

        <header className="flex flex-wrap items-center gap-3 mb-6">
          <div>
            <h1 className="text-3xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>Tableau de pilotage</h1>
            <p className="mt-1 capitalize" style={{ color: C.mut }}>{dateLabel}</p>
          </div>
          <div className="flex-1" />
          {nErr > 0 && !loading && (
            <button type="button" onClick={() => retry(Object.keys(errors))} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full pl-focus" style={{ background: '#FFE9E6', color: '#9A2B1E' }}
              title={Object.entries(errors).map(([k, e]) => `${k} : ${e.msg}`).join('\n')}>
              <AlertTriangle className="w-3.5 h-3.5" />{nErr} source{nErr > 1 ? 's' : ''} en erreur · réessayer
            </button>
          )}
          {hhmm && <span className="text-xs" style={{ color: C.mut }}>Mis à jour à {hhmm}</span>}
          <button type="button" onClick={() => setLiveOn(!liveOn)} aria-pressed={liveOn} title={liveOn ? 'Actualisation automatique toutes les 90 s — cliquer pour suspendre' : 'Actualisation automatique suspendue'}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-white rounded-xl pl-focus" style={{ border: `1px solid ${C.line}`, color: liveOn ? '#0E7C70' : C.mut }}>
            <span className="relative flex w-2 h-2"><span className={`absolute inset-0 rounded-full ${liveOn ? 'pl-pulse' : ''}`} style={{ background: liveOn ? C.teal : C.mut }} /><span className="relative w-2 h-2 rounded-full" style={{ background: liveOn ? C.teal : C.mut }} /></span>
            <Zap className="w-3.5 h-3.5" />{liveOn ? 'Live' : 'Pause'}
          </button>
          <button type="button" onClick={refresh} disabled={busy} aria-label="Actualiser les données" title="Actualiser"
            className="flex items-center justify-center w-10 h-10 bg-white rounded-xl pl-focus" style={{ border: `1px solid ${C.line}` }}>
            <RefreshCw className={`w-4 h-4 ${busy ? 'pl-spin' : ''}`} />
          </button>
          <span className="px-4 py-2 text-sm font-bold rounded-full" style={{ background: C.y, color: C.navy }}>{scope}</span>
          <Btn tone={edit ? 'navy' : 'ghost'} icon={edit ? Check : Settings2} onClick={() => setEdit(!edit)}>{edit ? 'Terminer' : 'Éditer'}</Btn>
          <Btn tone="navy" icon={Settings2} onClick={() => setConfigModal(true)}>Widgets</Btn>
        </header>

        {configModal && (
          <Modal title="Personnaliser le tableau de bord" onClose={() => setConfigModal(false)} wide footer={
            <>
              <Btn tone="ghost" onClick={() => setConfigModal(false)}>Annuler</Btn>
              <Btn tone="navy" icon={Check} onClick={() => { setConfigModal(false); flash('Configuration enregistrée'); }}>Enregistrer</Btn>
            </>
          }>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {ORDER.map((id) => {
                const w = WIDGETS[id];
                const selected = !prefs.hidden.includes(id);
                return (
                  <label key={id} className="flex items-center gap-3 p-4 cursor-pointer rounded-xl transition hover:bg-gray-50" style={{ border: `1px solid ${selected ? C.navy : C.line}`, background: selected ? C.bg : '#fff' }}>
                    <input type="checkbox" checked={selected} onChange={() => toggleWidget(id)} className="w-5 h-5 rounded" style={{ accentColor: C.navy }} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <w.icon className="w-4 h-4" style={{ color: w.color }} />
                        <span className="text-sm font-bold">{w.title}</span>
                      </div>
                      <span className="text-xs" style={{ color: C.mut }}>Taille min : {w.min}/12</span>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="mt-4 text-xs" style={{ color: C.mut }}>
              Cochez les widgets à afficher. Vous pouvez les réorganiser par glisser-déposer et les redimensionner. Vos choix sont mémorisés pour votre compte.
            </p>
          </Modal>
        )}

        <div className="pl-grid">
          {visible.map((id, i) => (
            <Shell key={id} id={id} w={WIDGETS[id]} idx={i} edit={edit} isXl={isXl} span={spanOf(id)} resizing={live?.id === id}
              collapsed={prefs.collapsed.includes(id)} dnd={{ drag, over, setDrag, setOver, drop }} summary={summary(id)}
              onMove={(d) => move(id, d)} onLive={(n) => setLive({ id, span: n })} onSpan={(n) => { setLive(null); setSpan(id, n); }}
              onHide={() => toggle('hidden', id)} onCollapse={() => toggle('collapsed', id)} onFocus={() => setFocus(id)}>
              {body(id)}
            </Shell>
          ))}
        </div>
        {visible.length === 0 && <Empty>Tous les widgets sont masqués. Cliquez sur « Personnaliser » pour les réafficher.</Empty>}

        {focus && <Modal wide title={WIDGETS[focus].title} onClose={() => setFocus(null)}>{body(focus)}</Modal>}
        {result && <ResultModal result={result} onClose={() => setResult(null)} />}

        {toast && (
          <div role="status" className="fixed z-50 max-w-md px-5 py-3 text-sm font-bold text-white shadow-xl pl-in right-6 bottom-6 rounded-2xl"
            style={{ background: toast.tone === 'err' ? '#B4301F' : C.navy }}>{toast.msg}</div>
        )}
      </div>
    </FmtCtx.Provider>
  );
};

export default PilotageDashboard;